import * as React from 'react';
import { NavLink, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as DateHelpers from './DateHelpers';
import * as _ from 'lodash';
import AllocateCancelModal from './AllocateCancelItem';
import AllocateCancelMultiModal from './AllocateCancelMultiItem';
import { CancelMultiDetails } from './models/JobSharedInterfaces';
import AllocateExtendModal from './AllocateExtendModal';
import AllocateAddItemModal from './AllocateAddItemModal';
import EditNoteModal from './EditNotes';
import AllocateLinkJobsModal from './AllocateLinkJobsModal';
import AllocateAssistTrucksModal from './AllocateAssistTrucks';
import CopyJobData from './ScheduleNewCopyJobModal';
import AllocatePlaceHolderData from './AllocatePlaceHolderModal';
import JobQuoteCalculatorModal from './JobQuoteCalculatorModal';
import * as Utils from './Utils';
import AllocateMultiTrucksModal from './AllocateMultiTrucks';
import MissingScheduleItemData from './AllocateMissingItems';
import AllocateChangeBranchModal from './AllocateChangeBranchModal';
import FlaggedJobsData from './ScheduleNewFlaggedJobsModal';
import JobInfoData from './ScheduleNewJobInfoModal';
import AllocateMoveMultipleItemsModal from './AllocateMoveMultipleItems';
import AllocateAssociatedJobsData from './AllocateAssociatedItems';
import JobQuoteDeliveryAddressData from './JobQuoteDeliveryAddressModal';
import AllocateMultiTrucksSimpleModal from './AllocateMultiTrucksSimple';
import JobScheduleVoidModalData from './JobScheduleVoidModal';
import ConfirmPopups from './ConfirmPopup';
import authService from './api-authorization/AuthorizeService';

interface Allocate {
    systemCompanyId: number;
    canEdit: boolean;
    canViewMissingOnHoldJobs: boolean;
    canAddManualJob: boolean;
    canAddFlaggedJobs: boolean;
    truckFilterId: number;
    branchFilterId: number;
    truckShowUnallocatedFilter: boolean;
    allocateItems: AllocateItem[];
    unallocatedItems: UnallocatedItem[];
    filteredItems: AllocateItem[];
    missingItemCount: number;
    onHoldItemCount: number;
    onHoldChanged: boolean;
    scheduleBranchOptions: GenericList[];
    itemTypeGroupOptions: GenericList[];
    truckOptions: TruckListOption[];
    availTruckOptions: TruckListOption[];
    filteredTrucks: TruckListOption[];
    cancelOptions: GenericList[];
    scheduleTypes: GenericList[];
    starterBayOptions: GenericList[];
    liftOptions: GenericList[];
    defaultDays: number;
    nextScheduleDate: string;
    allowAllocateToPastDate: boolean;   //check if user has permission to allocate items to the past
    allowChangeDefaultDays: boolean;
    showLinkJobsModal: boolean;
    linkedJobSiteAddress: string;
    linkedJobs: AllocatedJob[];
    showNewItemModal: boolean;
    newItem: AllocateNewItem;
    showMissingItems: boolean;
    showEditNotesModal: boolean;
    showExtendModal: boolean;
    extendDetails: ExtendDetails;
    showCancelModal: boolean;
    cancelDetails: CancelDetails;
    showMultiCancelModal: boolean;
    cancelMultiDetails: CancelMultiDetails;
    showMultipleTrucksSimple: boolean;
    showMultipleTrucks: boolean;
    multipleTruckItem: MultipleTruckItem;   //new
    showTruckAssist: boolean;
    truckAssistScheduleId: number;
    truckAssistTruckId: number;
    truckAssistTruck: string;
    truckAssistSiteAddress: string;
    truckAssistJobs: AssistTruckScheduleJob[];
    trucksAssisting: TruckListOption[];
    calcDisabled: boolean;
    showCopy: boolean;
    onOffhireDate: string;
    copyItem: CopyScheduleItem;
    placeHolderScheduleDate: string;
    showPlaceholderModal: boolean;
    voidSizeMin: number;
    showCalculator: boolean;
    calculatorIndex: number;
    calculatorItems: KwikstageCalculation[];
    calculatorKsItem: KwikstageItem;
    calculatorStatusId: number;
    calculatorTypeId: number;
    jobs: JobItem[];
    kwikstageJobs: KwikstageJobItem[];
    showChangeBranch: boolean;
    branchId: number;
    jobIndex: number;
    selectedJobs: boolean;
    showJobInfo: boolean;
    showVoidGearList: boolean;
    scheduleId: number;
    jobQuoteItemId: number;
    showFlaggedJobs: boolean;
    showMoveMultipleJobs: boolean;
    showMoveOnHireDate: boolean;
    showMoveOffHireDate: boolean;
    moveDetails: MoveDetails;
    showJobOnHold: boolean;
    selectAll: boolean;
    selectAllPrinted: boolean;
    showPrinted: boolean;
    showPending: boolean;
    showAssociatedItems: boolean;
    //update delivery address / invoice description / trolley required
    showDelAddress: boolean;
    deliveryAddress: DeliveryAddress;
    invoiceDescription: string;
    showInvoiceDescription: boolean;
    trolley: boolean;
    sortType: number;           //Customer = 1, Suburb = 2, Item = 3, OrigDate = 4
    sortDirection: number;     //1 means we are sorting asc and 2 desc
    totalJobs: number;
    showColourKey: boolean;
    errors: any;
    loading: boolean;
};

interface AllocateProps {
    scheduleDate: string;
    scheduleDateFormatted: string;
    scheduleBranchId: number;
    itemTypeGroupFilterId: number;
    updateScheduleDate(scheduleDate: string): void;
    updateItemTypeGroupFilter(filterId: number): void;
    goAllocateTruck(scheduleBranchId: number): void;
}

interface CopyScheduleItem {
    index: number;
    scheduleId: number;
    siteAddress: string;
    item: string;
    quantityScheduled: number;
    qtyType: string;
    unitOfMeasure: string;
    isKwikstage: boolean;
    kwikstageItems: KwikstageScheduleItem[];
    kwikstageOptions: KwikstageBayOptions[];
}

interface KwikstageScheduleItem {
    scheduleKwikstageId: number;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    starterBay: string;
    liftId: number;
    lift: string;
    baysTotal: number;
    bays: number;
}

interface KwikstageBayOptions {
    starterBayId: number;
    starterBay: string;
    liftOptions: KwikstageLiftOptions[];
}

interface KwikstageLiftOptions {
    liftId: number;
    lift: string;
}

interface SaveItem {
    onOffhireDate: string
    quantity: number;
    dates: string[];
}

interface CancelDetails {
    rowIndex: number;
    id: number;
    itemId: number;
    item: string;
    cancelReasonId: number;
}

interface ExtendDetails {
    rowIndex: number;
    id: number;
    typeId: number; //schedule type => 1: Install, 2: Pick up
    itemId: number;
    item: string;
    lastHireDate: string;
    existingLastHireDate: string;
    offhireDate: string;
    existingOffhireDate: string;
    existingPickupDate: string;
    existingOverhireRate: number;
    lastInvoiceDate: string;
    lastInvoiceDateFormatted: string;
    accountTypeId: number;
    purchaseOrderNumber: string;
    extendNoDays: number;
    newLastHireDate: string;
    newOffhireDate: string;
    newPickupDate: string;
    invoiceAmount: number;
    discountPercent: number;
    publicHolidays: number;
    finalInvoiceAmount: number;
}

interface GenericList {
    id: number;
    name: string;
}

interface TruckListOption {
    id: number;
    branchId: number;
    scheduleBranchId: number;
    name: string;
    registrationNumber: string;
    scheduleVehicleId: number;
    teamDisplay: string;
    numberTeamMembers: number;
}

interface KwikstageCalculation {
    id: number;
    starterBayId: number;
    liftId: number;
    bays: number;
    baysRemaining: number;
}

interface AllocateItem {
    id: number;
    itemIndex: number;
    jobId: string;
    itemStatusId: number;
    scheduleDate: string;
    scheduleDateFormatted: string;
    scheduledDateOriginalFormatted: string;
    scheduledDateOriginalRed: boolean;
    scheduledDateOriginalOrange: boolean;
    onhireDate: string;
    onhireDateFormatted: string;
    offhireDate: string;
    offhireDateFormatted: string;
    customer: string;
    siteAddress: string;
    suburb: string;
    siteAddressActual: string;
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
    invoiceDescription: string;
    trolley: boolean;
    description: string;
    manualItem: boolean;
    jobQuoteItemId: number;
    itemId: number;
    item: string;
    itemTypeId: number;
    itemShortcut: string;
    extraHours: number;
    itemInstallFormula: number; //used to calculate estimated time
    itemPickupFormula: number;
    itemVoidFormula: number;
    itemVoidExtraTime: number;
    itemIsVoid: boolean;
    itemIsKwikstage: boolean;
    itemIsEvent: boolean;       //true if job is an event or item is an event item
    frequencyId: number;
    hasAssociatedItems: boolean;
    hasAssociatedItemsToComplete: boolean;
    typeId: number;
    bays: number;       //# bays for kwikstage
    size: number;       //size of the void
    sizeBays: string;   //size of void or # bays for kwikstage
    quantityTotal: number;
    quantityScheduled: number;
    unitOfMeasure: string;
    quantityType: string;
    scheduleBranchId: number;
    truck: string;
    truckId: number;
    craneTruckRequired: boolean;
    teamDisplay: string;
    vehicleItemOrder: number;
    estimatedTime: string;
    numberTeamMembers: number;
    notes: string;
    showRedNotes: boolean;
    showGreenNotes: boolean;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    goaheadRequired: boolean;
    runsheetChecked: boolean;
    allowEdit: boolean;
    allowExtend: boolean;
    allowOnHold: boolean;
    allowDelete: boolean;
    jobStarted: boolean;
    callout: boolean;
    isPlaceholder: boolean;
    isCustomerPickup: boolean;
    printed: boolean;
    linkedJobId: number;
    truckAssistScheduleId: number;
    needsPo: boolean;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
    warning: boolean;
    locked: boolean;
    selected: boolean;
}

interface AllocateNewItem {
    jobId: number;
    siteAddress: string;
    googleAddress: string;
    googleLatitude: number;
    googleLongitude: number;
    customerName: string;
    contactName: string;
    scheduleDate: string;
    scheduleBranchId: number;
    description: string;
    typeId: number;
    itemTypeGroupId: number;
    notes: string;
    runsheetNotes: string;
    subId: string;
}

interface MultipleTruckItem {
    rowIndex: number;
    jobId: string;
    id: number;     //schedule id of the item we are editing
    itemId: number;
    item: string;
    originalTotal: number;
    originalKwikstage: KwikstageScheduleItem[];
    originalTruckId: number;
    selectedTrucks: TruckListOption[];
    formula: number;
    size: number;
    voidSizeMin: number;
    itemVoidExtraTime: number;
    multipleTrucks: MultipleTruck[];
    isKwikstage: boolean;
    isVoid: boolean;
    scheduleType: number;
    extraHours: number;
}

interface MultipleTruck {
    scheduleId: number;     //schedule id as once saved, each allocation will have its own schedule id
    truckId: number;
    truckNumberTeamMembers: number;
    startTime: string;
    quantityTotal: number;
    quantityScheduled: number;
    estimatedTime: string;
    estimatedTimeMins: number;
    updateOrder: boolean;
    vehicleItemOrder: number;
    kwikstageItems: KwikstageScheduleItem[];
}

interface AllocatedJob {
    selected: boolean;
    jobId: string;
    siteAddress: string;
    linkedJobId: number;
}

interface AssistTruckScheduleJob {
    id: number;     //schedule id of the item we are editing
    selected: boolean;
    jobQuoteItemId: number;
    jobId: string;
    siteAddress: string;
    itemId: number;
    item: string;
    itemShortcut: string;
    truck: string;
    truckId: number;
    quantityScheduled: number;
}

interface JobItem {
    itemIndex: number;
    scheduleDate: string;
    quantity: number;
    warning: boolean;
    locked: boolean;
}

interface KwikstageJobItem {
    scheduleDate: string;
    jobQuoteItemCalculationId: number;
    starterBayId: number;
    liftId: number;
    baysTotal: number;
    bays: number;
    maxBays: number;
    warning: boolean;
    locked: boolean;
}

interface KwikstageItem {
    extraHours: number;
    kwikstageInstalledFor: number;
    quoteNote: string;
}

interface UnallocatedItem {
    scheduleBranchId: string;
    itemTypeGroup: string;
    totalJobs: number;
}

interface MoveDetails {
    scheduleDate: string;
    onhireDate: string;
    offhireDate: string;
}

interface DeliveryAddress {
    deliveryAddress: string;
    deliveryAddressGooglePlaceId: string;
    deliveryAddressLatitude: number;
    deliveryAddressLongitude: number;
};

export default class AllocateDetails extends React.Component<AllocateProps, Allocate> {
    constructor(props: any) {
        super(props);

        this.state = {
            systemCompanyId: 2,
            canEdit: false,
            canViewMissingOnHoldJobs: false,
            canAddManualJob: false,
            canAddFlaggedJobs: false,
            truckFilterId: -1,
            branchFilterId: -1,
            truckShowUnallocatedFilter: false,
            allocateItems: [],
            unallocatedItems: [],
            filteredItems: [],
            missingItemCount: 0,
            onHoldItemCount: 0,
            onHoldChanged: false,
            scheduleBranchOptions: [],
            itemTypeGroupOptions: [],
            truckOptions: [],
            availTruckOptions: [],
            filteredTrucks: [],
            cancelOptions: [],
            scheduleTypes: [],
            starterBayOptions: [],
            liftOptions: [],
            defaultDays: 2,
            nextScheduleDate: "",
            allowAllocateToPastDate: false,
            allowChangeDefaultDays: false,
            showLinkJobsModal: false,
            linkedJobSiteAddress: "",
            linkedJobs: [],
            showNewItemModal: false,
            newItem: {
                jobId: 0,
                siteAddress: "",
                googleAddress: "",
                googleLatitude: 0,
                googleLongitude: 0,
                customerName: "",
                contactName: "",
                scheduleDate: "",
                scheduleBranchId: 1,
                description: "",
                typeId: -1,
                itemTypeGroupId: -1,
                notes: "",
                runsheetNotes: "",
                subId: ""
            },
            showMissingItems: false,
            showEditNotesModal: false,
            showExtendModal: false,
            extendDetails: {
                rowIndex: 0,
                id: 0,
                typeId: 0,
                itemId: 0,
                item: "",
                lastHireDate: "",
                existingLastHireDate: "",
                offhireDate: "",
                existingOffhireDate: "",
                existingPickupDate: "",
                existingOverhireRate: 0,
                lastInvoiceDate: "",
                lastInvoiceDateFormatted: "",
                accountTypeId: 0,
                purchaseOrderNumber: "",
                extendNoDays: 0,
                newLastHireDate: "",
                newOffhireDate: "",
                newPickupDate: "",
                invoiceAmount: 0,
                discountPercent: 0,
                publicHolidays: 0,
                finalInvoiceAmount: 0
            },
            showCancelModal: false,
            cancelDetails: {
                rowIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                cancelReasonId: -1
            },
            showMultiCancelModal: false,
            cancelMultiDetails: {
                rowIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                cancelQty: 0,
                scheduleDateFormatted: "",
                typeId: 0
            },
            showMultipleTrucksSimple: false,
            showMultipleTrucks: false,
            multipleTruckItem: {
                rowIndex: 0,
                jobId: "",
                id: 0,
                itemId: 0,
                item: "",
                originalTotal: 0,
                originalTruckId: -1,
                originalKwikstage: [],
                selectedTrucks: [],
                isKwikstage: false,
                isVoid: false,
                formula: 0,
                size: 0,
                itemVoidExtraTime: 0,
                voidSizeMin: 0,
                scheduleType: -1,
                extraHours: 0,
                multipleTrucks: []
            },
            showTruckAssist: false,
            truckAssistScheduleId: 0,
            truckAssistTruckId: -1,
            truckAssistTruck: "",
            truckAssistSiteAddress: "",
            truckAssistJobs: [],
            trucksAssisting: [],
            calcDisabled: false,
            showCopy: false,
            onOffhireDate: "",
            copyItem: {
                index: -1,
                scheduleId: 0,
                item: "",
                siteAddress: "",
                quantityScheduled: 0,
                qtyType: "",
                unitOfMeasure: "",
                isKwikstage: false,
                kwikstageItems: [],
                kwikstageOptions: []
            },
            placeHolderScheduleDate: "",
            showPlaceholderModal: false,
            voidSizeMin: 0,
            showCalculator: false,
            calculatorIndex: -1,
            calculatorItems: [],
            calculatorKsItem: {
                kwikstageInstalledFor: -1,
                extraHours: 0,
                quoteNote: ""
            },
            calculatorStatusId: -1,
            calculatorTypeId: -1,
            jobs: [],
            kwikstageJobs: [],
            showChangeBranch: false,
            branchId: -1,
            jobIndex: -1,
            selectedJobs: false,
            showJobInfo: false,
            showVoidGearList: false,
            scheduleId: -1,
            jobQuoteItemId: -1,
            showFlaggedJobs: false,
            showMoveMultipleJobs: false,
            showMoveOnHireDate: true,
            showMoveOffHireDate: false,
            moveDetails: {
                scheduleDate: "",
                onhireDate: "",
                offhireDate: "",
            },
            showJobOnHold: false,
            selectAll: false,
            selectAllPrinted: false,
            showPrinted: false,
            showPending: true,
            showAssociatedItems: false,
            showDelAddress: false,
            deliveryAddress: {
                deliveryAddress: "",
                deliveryAddressGooglePlaceId: "",
                deliveryAddressLatitude: 0,
                deliveryAddressLongitude: 0
            },
            invoiceDescription: "",
            showInvoiceDescription: false,
            trolley: false,
            sortType: 0,
            sortDirection: 0,
            totalJobs: 0,
            showColourKey: false,
            errors: {},
            loading: true
        };
    }

    componentDidMount() {
        this.getScheduledItems();
    }

    async getScheduledItems() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //gets items for allocating as well as all the dropdowns
        axios.get('api/Allocate/GetAllocatingItems?ScheduleDate=' + this.props.scheduleDate + '&ScheduleBranchId=' + this.props.scheduleBranchId + '&ItemTypeGroupId=' + this.props.itemTypeGroupFilterId + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var allocateItems = this.setAllocateData(res.data.allocateItems);
            var filteredTrucks: TruckListOption[] = [];
            var scheduleBranchOptions = res.data.scheduleBranchOptions;

            //default branch if user only has access to one branch
            var branchFilterId = this.props.scheduleBranchId < 0 ? scheduleBranchOptions[0].id : this.props.scheduleBranchId;
            filteredTrucks = res.data.truckOptions;
            filteredTrucks = filteredTrucks.filter(x => x.scheduleBranchId == branchFilterId);

            this.setState({
                systemCompanyId: res.data.systemCompanyId, scheduleTypes: res.data.scheduleTypes, scheduleBranchOptions: res.data.scheduleBranchOptions,
                itemTypeGroupOptions: res.data.itemTypeGroupOptions, branchFilterId: branchFilterId, truckOptions: res.data.truckOptions,
                filteredTrucks: filteredTrucks, starterBayOptions: res.data.starterBayOptions, liftOptions: res.data.liftOptions, truckFilterId: -1,
                allocateItems: allocateItems, unallocatedItems: res.data.unallocatedJobs, missingItemCount: res.data.missingItemCount,
                onHoldItemCount: res.data.onHoldItemCount, filteredItems: [], allowAllocateToPastDate: res.data.allowAllocateToPastDate,
                allowChangeDefaultDays: res.data.allowChangeDefaultDays, voidSizeMin: res.data.voidSizeMin, defaultDays: res.data.defaultDays,
                nextScheduleDate: res.data.nextScheduleDate, totalJobs: res.data.totalJobs, canEdit: res.data.canEdit, canAddManualJob: res.data.canAddManualJob, 
                canViewMissingOnHoldJobs: res.data.canViewMissingOnHoldJobs, canAddFlaggedJobs: res.data.canAddFlaggedJobs, errors: {}, loading: false
            })
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //Sort Type: Customer = 1, Suburb = 2, Item = 3, Orig Date = 4
    //Sort Direction: 1 = Asc, 2 = Desc
    async getAllocateItems(branchId: number, itemTypeGroupId : number, truckId: number, showUnallocated: boolean, scheduleDate: string, showPrinted: boolean, showPending: boolean, sortType: number, sortDirection: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //gets only the items for allocating
        axios.get('api/Allocate/getAllocateItems?ScheduleBranchId=' + branchId + '&ItemTypeGroupId=' + itemTypeGroupId + '&TruckId=' + truckId + '&ShowUnallocated=' + showUnallocated + '&ScheduleDate=' + scheduleDate + '&ShowPrinted=' + showPrinted + '&ShowPending=' + showPending + '&SortType=' + sortType + '&SortDirection=' + sortDirection + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var allocateItems = this.setAllocateData(res.data.allocateItems);
            var filteredItems = allocateItems;
            if (truckId > 0 && showUnallocated) {
                filteredItems = allocateItems.filter(data => data.truckId == truckId || data.truckId == -1);
            } else if (truckId > 0) {
                filteredItems = allocateItems.filter(data => data.truckId == truckId);
            }
            var filteredTrucks: TruckListOption[] = res.data.truckOptions;
            filteredTrucks = filteredTrucks.filter(x => x.scheduleBranchId == branchId);
            this.setState({ allocateItems: allocateItems, filteredItems: filteredItems, unallocatedItems: res.data.unallocatedJobs, truckOptions: res.data.truckOptions, filteredTrucks: filteredTrucks, truckFilterId: truckId, truckShowUnallocatedFilter: showUnallocated, totalJobs: res.data.totalJobs, selectAll: false, selectAllPrinted: false, sortType: sortType, sortDirection: sortDirection, errors: {} });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    setAllocateData = (allocateItems: AllocateItem[]) => {
        for (var i = 0; i < allocateItems.length; i++) {
            allocateItems[i].itemIndex = i;
        }
        return allocateItems;
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);
    }

    handleShowPrintedChange = (e: any) => {
        this.setState({ showPrinted: e.target.checked });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, e.target.checked, this.state.showPending, 0, 0);
    }

    handleShowPendingChange = (e: any) => {
        this.setState({ showPending: e.target.checked });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, this.state.showPrinted, e.target.checked, 0, 0);
    }

    handleDaysChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);

        this.saveDefaultDays(e.target.value * 1);
    }

    async saveDefaultDays(days: number) {
        const token = await authService.getAccessToken();

        var defaultDays = {
            days: days
        };

        //save change
        axios.post('api/Allocate/SaveDefaultDays', defaultDays, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ nextScheduleDate: res.data });
            toast.success("Next available date saved successfully");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    handleScheduleDateChange = (e: any) => {
        this.props.updateScheduleDate(e.target.value);
    }

    //branch filter change
    handleBranchChange = (e: any) => {
        //make sure we save the data first
        this.saveData(false);

        this.getAllocateItems(e.target.value, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
        var filteredTrucks: TruckListOption[] = [];
        if (e.target.value > 0) {
            filteredTrucks = this.state.truckOptions.filter(x => x.branchId == e.target.value);
        }
        this.setState({ branchFilterId: e.target.value, filteredTrucks: filteredTrucks, truckFilterId: -1, truckShowUnallocatedFilter: false, errors: {} });
    }

    //item type filter change
    handleItemTypeGroupChange = (e: any) => {
        var itemTypeGroupId = e.target.value * 1;
        this.props.updateItemTypeGroupFilter(itemTypeGroupId);
        this.setState({ truckFilterId: -1, errors: {} });

        //make sure we save the data first
        this.saveData(false);
        this.getAllocateItems(this.state.branchFilterId, itemTypeGroupId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    //truck filter change
    handleTruckChange = (e: any) => {
        var truckFilter = e.target.value * 1;
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, truckFilter, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    //select to show unallocated when filtering
    handleShowUnallocatedChange = (e: any) => {
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, e.target.checked, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    //Move Jobs
    moveJobs = (e: any) => {
        e.preventDefault();

        var jobsSelected = this.state.allocateItems.filter(a => a.selected);
        if (jobsSelected.length > 0) {
            this.saveData(false);
            let moveDate = DateHelpers.addBusinessDaysToDate(this.props.scheduleDate, 1);
            let moveDetails: MoveDetails = {
                scheduleDate: moveDate,
                onhireDate: moveDate,
                offhireDate: ""
            };

            //check if there are any installs - these need on hire date changed
            var hasInstallItems = jobsSelected.filter(a => a.typeId === 1).length > 0;
            var hasPickupItems = jobsSelected.filter(a => a.typeId === 2);
            var hasPickupItem = hasPickupItems.length === 1;    //can only show off hire date if we are moving a single pickup
            if (hasPickupItem) {
                var pickup = hasPickupItems[0];
                moveDetails.offhireDate = pickup.offhireDate;
            }

            this.setState({ showMoveMultipleJobs: true, moveDetails: moveDetails, showMoveOnHireDate: hasInstallItems, showMoveOffHireDate: hasPickupItem });
        } else {
            toast.error("Please tick jobs you wish to move to another day!");
        }
    }

    hideMoveJobs = () => {
        let moveDetails: MoveDetails = {
            scheduleDate: "",
            onhireDate: "",
            offhireDate: ""
        };
        this.setState({ showMoveMultipleJobs: false, moveDetails: moveDetails, showMoveOnHireDate: true, showMoveOffHireDate: false });
    }

    updateMoveJobs = (updatedMoveDetails: MoveDetails) => {
        this.setState({ moveDetails: updatedMoveDetails });
    }

    saveMoveJobs = () => {
        this.saveJobsMoving();
    }

    saveJobsMoving = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobsSelected = this.state.allocateItems.filter(a => a.selected);
        var jobScheduleIds: number[] = [];
        var jobsNotMoved: string[] = [];
        for (var i = 0; i < jobsSelected.length; i++) {
            //validate any pickups to make sure we can move them - offhire date needs to be before new schedule date
            var job = jobsSelected[i];
            var jobCanBeMoved = true;
            if (job.typeId === 2 && this.state.moveDetails.offhireDate === "") {
                //pickup with no off hire date selected which means we are keeping the existing off hire date
                //validate that the existing offhire date is <= new schedule date
                if (!DateHelpers.compareDate(job.offhireDate, this.state.moveDetails.scheduleDate)) {
                    jobsNotMoved.push(job.siteAddress);
                    jobCanBeMoved = false;  //user needs to move this job separately as off hire date is > schedule date
                }
            }

            if (jobCanBeMoved) {
                jobScheduleIds.push(job.id);
            }
        }

        var moveItems = {
            scheduleDate: this.state.moveDetails.scheduleDate,
            onHireDate: this.state.moveDetails.onhireDate,
            offHireDate: this.state.moveDetails.offhireDate,
            scheduleIds: jobScheduleIds,
            subId: user.sub
        };

        axios.post('api/Allocate/SaveMoveJobs', moveItems, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (jobsNotMoved.length > 0) {
                var errorMsg = "Please move the following jobs separately as offhire date MUST be updated:\n";
                for (var j = 0; j < jobsNotMoved.length; j++) {
                    errorMsg += jobsNotMoved[j] + "\n";
                }
                toast.error(errorMsg);
            } else {
                toast.success("Jobs have been moved successfully");
            }

            let moveDetails: MoveDetails = {
                scheduleDate: "",
                onhireDate: "",
                offhireDate: ""
            };
            this.setState({ showMoveMultipleJobs: false, moveDetails: moveDetails, showMoveOnHireDate: true, showMoveOffHireDate: false });
            this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //End Move Jobs

    //Sort Jobs
    handleSort = (e: any, columnSorted: number, sortDir: number) => {
        e.preventDefault();
        let existColSorted = this.state.sortType;
        if (existColSorted == columnSorted) {
            //change direction only
            this.setState({ sortDirection: sortDir });
        } else {
            //sort new column ascending
            this.setState({ sortType: columnSorted, sortDirection: sortDir });
        }
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, columnSorted, sortDir);
    }

    //End Sort Jobs

    //Cancel Popup
    cancelItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];
        if (allocateItem) {
            if (allocateItem.id == 0) {
                //just remove it from the grid as not saved
                allocateItems.splice(itemIndex, 1);
                this.setState({ allocateItems: allocateItems });
                toast.success("Item removed successfully");
            } else {
                this.getCancelReasons(itemIndex);
            }
        }
    }

    async getCancelReasons(itemIndex: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        let errors: any = {};
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        //get the list of cancel reasons
        axios.get("api/Allocate/CheckCancelOptions?SubId=" + user.sub + "&Id=" + allocateItem.id + '&JobId=' + allocateItem.jobId + '&JobQuoteItemId=' + allocateItem.jobQuoteItemId + '&ScheduleType=' + allocateItem.typeId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var cancelReasons = res.data.cancelReasons;
            var cancelOption = res.data.cancelOption;

            if (cancelOption === 1) {
                //show popup to cancel

                let cancelDetails: CancelDetails = {
                    rowIndex: itemIndex,
                    id: allocateItem.id,
                    itemId: allocateItem.itemId,
                    item: allocateItem.item,
                    cancelReasonId: -1
                };

                this.setState({ showCancelModal: true, cancelDetails: cancelDetails, cancelOptions: cancelReasons });
            } else if (cancelOption === 2) {
                //show popup to confirm moving total to existing job

                let cancelDetails: CancelMultiDetails = {
                    rowIndex: itemIndex,
                    id: allocateItem.id,
                    itemId: allocateItem.itemId,
                    item: allocateItem.item,
                    cancelQty: allocateItem.quantityScheduled,
                    scheduleDateFormatted: res.data.scheduleDateFormatted,
                    typeId: allocateItem.typeId
                };

                this.setState({ showMultiCancelModal: true, cancelMultiDetails: cancelDetails });
            } else if (cancelOption === 3) {
                //can't cancel
                toast.error("Cannot cancel this item, see error on screen!");
                var errorString = "Row " + (itemIndex + 1) + " cannot be cancelled as the remaining qty must be installed to create the Initial Invoice.";
                errorString += "\n";
                errorString += "If total Qty is incorrect, use Edit Job on Runsheet for this item scheduled on " + res.data.scheduleDateFormatted + "to edit quantites so invoice will have correct price.";
                errorString += "After using Edit Job YOU MUST then cancel this item in order for the Initial Invoice to be created!"
                errors["allocatedItems"] = errorString;
                this.setState({ errors: errors });
            } else {
                toast.error("Cannot cancel this item as the customer is not setup for Overhire. Extend job instead.")
                errors["allocatedItems"] = "Cannot cancel this item as the customer is not setup for Overhire. Extend job instead.";
                this.setState({ errors: errors });
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    resetCancelItems = () => {
        let cancelDetails = {
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            cancelReasonId: -1
        };
        return cancelDetails;
    }

    hideCancelModal = () => {
        let cancelDetails = this.resetCancelItems();
        this.setState({ showCancelModal: false, cancelDetails: cancelDetails });
    }

    updateCancelData = (updatedCancelDetails: CancelDetails) => {
        let cancelDetails = this.state.cancelDetails;
        cancelDetails.cancelReasonId = updatedCancelDetails.cancelReasonId;
        this.setState({ cancelDetails: cancelDetails });         
    }

    saveCancel = () => {
        this.saveCancelItem();
    }

    async saveCancelItem() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //cancel the item 
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[this.state.cancelDetails.rowIndex];
        if (allocateItem) {

            var cancelItem = {
                scheduleId: allocateItem.id,
                jobId: allocateItem.jobId,
                itemId: allocateItem.itemId,
                item: allocateItem.item,
                cancelReasonId: this.state.cancelDetails.cancelReasonId,
                manualItem: allocateItem.manualItem,
                cancelJobStatus: false,
                subId: user.sub
            };

            axios.post('api/Allocate/CancelItem', cancelItem, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                let cancelDetails = this.resetCancelItems();
                this.setState({ showCancelModal: false, cancelDetails: cancelDetails });
                toast.success("Allocation Item Cancelled");
                
                this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
    }
    //End Cancel Popup

    //Cancel Multi Popup 
    hideCancelMultiModal = () => {
        let cancelDetails = this.resetCancelMultiItems();
        this.setState({ showMultiCancelModal: false, cancelMultiDetails: cancelDetails });
    }

    resetCancelMultiItems = () => {
        let cancelDetails: CancelMultiDetails = {
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            cancelQty: 0,
            scheduleDateFormatted: "",
            typeId: 0
        };
        return cancelDetails;
    }

    allocateCancelItem = () => {
        this.saveCancelAllocation();
    }

    async saveCancelAllocation() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[this.state.cancelMultiDetails.rowIndex];

        var cancelItem = {
            scheduleId: allocateItem.id,
            jobQuoteItemId: allocateItem.jobQuoteItemId,
            jobId: allocateItem.jobId,
            itemId: allocateItem.itemId,
            item: allocateItem.item,
            quantity: allocateItem.quantityScheduled,
            typeId: allocateItem.typeId,
            subId: user.sub
        };

        axios.post('api/Allocate/CancelAllocateExistItem', cancelItem, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Allocation Item Cancelled and Quantity added to existing item");
            let cancelDetails = this.resetCancelMultiItems();
            this.setState({ showMultiCancelModal: false, cancelMultiDetails: cancelDetails });
            this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //End Cancel Multi Popup

    //EDIT NOTES
    hideEditNotesModal = () => {
        this.setState({ showEditNotesModal: false, scheduleId: -1, jobIndex: -1 });
    }

    editNotes = (e: any, itemIndex: number) => {
        e.preventDefault();
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        this.setState({ showEditNotesModal: true, scheduleId: allocateItem.id, jobIndex: itemIndex, errors: {} });
    }

    saveNotes = (scheduleNotes: string, timestamp: Uint8Array) => {
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[this.state.jobIndex];

        toast.success("Run Sheet Notes Saved");
        
        allocateItem.rowVersionTimestamp = timestamp;
        allocateItem.notes = scheduleNotes;

        this.setState({ showEditNotesModal: false, allocateItems: allocateItems, scheduleId: -1, jobIndex: -1, errors: {} });
        
    }
    //END EDIT NOTES

    //Link Jobs together
    openLinkJobModal = (e: any) => {
        e.preventDefault();
        //remove manual jobs as they cannot be linked
        var allocateItems = this.state.allocateItems.filter(a => !a.manualItem);
        var allocatedJobs: AllocatedJob[] = [];

        for (var i = 0; i < allocateItems.length; i++) {
            //exclude any ids that are set to the current job id as I'm just using these for display on screen to make it easier to see what jobs are all linked
            var existLinkedJobId = allocateItems[i].jobId.toString() === allocateItems[i].linkedJobId.toString() ? 0 : allocateItems[i].linkedJobId;

            let jobExists = allocatedJobs.findIndex(j => j.jobId === allocateItems[i].jobId);
            if (jobExists < 0) {
                //add unique jobs
                var allocatedJob: AllocatedJob = {
                    jobId: allocateItems[i].jobId,
                    siteAddress: allocateItems[i].siteAddress,
                    linkedJobId: existLinkedJobId,
                    selected: false
                };
                allocatedJobs.push(allocatedJob);
            }
        }

        this.setState({ linkedJobs: allocatedJobs, linkedJobSiteAddress: "", showLinkJobsModal: true });
    }

    hideLinkJobModal = () => {
        this.setState({ linkedJobs: [], linkedJobSiteAddress: "", showLinkJobsModal: false });
    }

    updateLinkModalSiteAddress = (siteAddress: string) => {
        this.setState({ linkedJobSiteAddress: siteAddress });
    }

    updateLinkJobModal = (linkedJobs: AllocatedJob[]) => {
        this.setState({ linkedJobs: linkedJobs });
    }

    saveLinkJobModal = () => {
        var linkedJobs = this.state.linkedJobs.filter(j => j.selected);

        if (linkedJobs.length > 1) {
            this.saveLinkedJob(linkedJobs);
        } else {
            toast.error("You must select at least 2 jobs in order to link!");
        }

    }

    async saveLinkedJob(linkedJobs: AllocatedJob[]) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var saveLinkedJobs = {
            linkJobDetails: linkedJobs,
            subId: user.sub
        };

        //must have at least two jobs to link
        axios.post('api/Allocate/LinkJobs', saveLinkedJobs, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                //update exisitng items with new linked jobs
                var allocatedItems = this.state.allocateItems;
                var linkedJobs = res.data.linkJobDetails;

                for (var i = 0; i < linkedJobs.length; i++) {
                    //find jobs and update
                    var allocatedJobs = allocatedItems.filter(ai => ai.jobId == linkedJobs[i].jobId);
                    for (var x = 0; x < allocatedJobs.length; x++) {
                        //updated allocated job with new linked job id
                        allocatedJobs[x].linkedJobId = linkedJobs[i].linkedJobId;
                    }
                }

                this.setState({ allocateItems: allocatedItems, linkedJobs: [], linkedJobSiteAddress: "", showLinkJobsModal: false });
                toast.success("Jobs have been linked");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveUnlinkJob = (linkedJobs: AllocatedJob[], jobId: string) => {
        //reset linked Job Id on jobs that were unlinked
        var allocateItems = this.state.allocateItems;
        var unlinkItems = allocateItems.filter(ai => ai.jobId == jobId);
        for (var i = 0; i < unlinkItems.length; i++) {
            unlinkItems[i].linkedJobId = 0;
        }

        this.setState({ linkedJobs: linkedJobs, allocateItems: allocateItems });
    }

    //End Link Jobs together

    //Add New Item Modal 
    hideNewItemModal = () => {
        //reset data
        var newItem = this.resetNewItem();

        this.setState({ newItem: newItem, showNewItemModal: false, errors: {} });
    }

    openNewItemModal = (e: any) => {
        e.preventDefault();

        let newItem = this.state.newItem;
        newItem.scheduleDate = this.props.scheduleDate;

        this.setState({ newItem: newItem, showNewItemModal: true, errors: {} });
    }

    updateNewItemData = (updatedNewItem: AllocateNewItem) => {
        this.setState({ newItem: updatedNewItem });
    }

    resetNewItem = () => {
        var newItem: AllocateNewItem = {
            jobId: 0,
            siteAddress: "",
            googleAddress: "",
            googleLatitude: 0,
            googleLongitude: 0,
            customerName: "",
            contactName: "",
            scheduleDate: "",
            scheduleBranchId: 1,
            description: "",
            typeId: -1,
            itemTypeGroupId: -1,
            notes: "",
            runsheetNotes: "",
            subId: ""
        }
        return newItem;
    }

    saveNewItemData = () => {
        this.saveNewAllocation();
    }

    async saveNewAllocation() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //get default branch
        axios.get('api/User/GetUserDefaultScheduleBranch?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var newItem = this.state.newItem;
            newItem.scheduleBranchId = res.data;   //default to logged in users branch, then will update when allocate truck
            newItem.subId = user.sub;
            axios.post('api/Allocate/SaveNewAllocationItem', newItem, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                toast.success("New Item Added Successfully");
                var newItem = this.resetNewItem();
                var allocatedItems = this.state.allocateItems;
                var addedItem = res.data.newItem;
                addedItem.itemIndex = allocatedItems.length;
                allocatedItems.push(addedItem);
                this.setState({ allocateItems: allocatedItems, newItem: newItem, showNewItemModal: false, errors: {} });
            })
            .catch(error => {
                toast.error(error.message);
            });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //End New Item Modal

    //Schedule Missing Jobs 
    scheduleMissingItems = () => {
        this.setState({ showMissingItems: true, onHoldChanged: true });
    }

    hideMissingItems = () => {
        this.setState({ showMissingItems: false });
    }

    setMissingItemsChanged = () => {
        let onHoldRefresh = this.state.onHoldChanged
        this.setState({ onHoldChanged: !onHoldRefresh });
    }

    saveMissingItems = (noMissingItems: number, noOnHoldItems: number) => {
        toast.success("Missing / On Hold Items Added Successfully");
        this.setState({ missingItemCount: noMissingItems, onHoldItemCount: noOnHoldItems, showMissingItems: false });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    //End Schedule Missing Jobs

    //Extend Modal
    extendModal = (e: any, itemIndex: number) => {
        e.preventDefault();
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        this.setState({ showExtendModal: true, scheduleId: allocateItem.id, jobQuoteItemId: allocateItem.jobQuoteItemId });
    }

    hideExtendModal = () => {
        this.setState({ showExtendModal: false, scheduleId: 0, jobQuoteItemId: 0 });
    }

    saveExtendData = () => {
        this.setState({ showExtendModal: false, scheduleId: 0, jobQuoteItemId: 0 });
        toast.success("Allocation Schedule Extended Successfully");
        this.getScheduledItems();
    }
    //End Extend Modal

    //Job on Hold
    placeJobOnHold = (e: any, itemIndex: number) => {
        e.preventDefault();

        this.setState({ showJobOnHold: true, jobIndex: itemIndex });
    }

    hideJobOnHold = () => {
        this.setState({ showJobOnHold: false, jobIndex: -1 });
    }

    saveJobOnHold = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobs = this.state.allocateItems;
        var job = jobs[this.state.jobIndex];

        var allocate = {
            id: job.id,
            subId: user.sub
        };

        axios.post('api/Allocate/PlaceItemOnHold', allocate, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                this.setState({ showJobOnHold: false, jobIndex: -1 });
                this.setMissingItemsChanged();
                this.getScheduledItems();
                toast.success("Item placed on hold Successfully");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //End Job on Hold

    //Allocate Multiple Trucks
    allocateMultipleTrucks = (e: any, itemIndex: number) => {
        e.preventDefault();

        this.allocateMultiTruck(itemIndex);
    }

    async allocateMultiTruck(itemIndex: number) {
        const token = await authService.getAccessToken();

        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];
        var truckOptions = this.state.truckOptions;

        //get truck data
        axios.get("api/Allocate/GetMultipleTrucks?ScheduledDate=" + this.props.scheduleDate + "&ScheduleId=" + allocateItem.id + "&JobQuoteItemId=" + allocateItem.jobQuoteItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var originalKs = res.data.originalKwikstageItems;
            var multipleTrucks = res.data.multipleTrucks;
            var selectedTruckIds = res.data.selectedTruckIds;
            var totalQty = 0;
            //add the number of team members per truck
            for (var i = 0; i < multipleTrucks.length; i++) {
                var truck = this.state.truckOptions.filter(data => data.id == multipleTrucks[i].truckId);
                if (truck.length > 0) {
                    multipleTrucks[i].truckNumberTeamMembers = truck[0].numberTeamMembers;
                }
                totalQty += multipleTrucks[i].quantityScheduled;
            }

            //calculate the original time - based on 3 people in truck
            let formula = 0;
            if (allocateItem.itemIsVoid) {
                formula = allocateItem.itemVoidFormula;
            } else {
                formula = allocateItem.typeId === 1 ? allocateItem.itemInstallFormula : allocateItem.itemPickupFormula;
            }

            if (totalQty === 0 && allocateItem.quantityScheduled === 0) {
                //nothing has been scheduled so use total qty
                totalQty = allocateItem.quantityTotal;
            } else if (totalQty === 0 && allocateItem.quantityScheduled !== 0) {
                totalQty = allocateItem.quantityScheduled;  //cater for part installs and part pickups
            }

            //get selected trucks
            var selectedTrucks: TruckListOption[] = [];
            for (var x = 0; x < selectedTruckIds.length; x++) {
                var selectTruck = truckOptions.find(t => t.id === selectedTruckIds[x]);
                if (selectTruck) {
                    selectedTrucks.push(selectTruck);
                }
            }

            var multipleTrucksItem: MultipleTruckItem = {
                rowIndex: itemIndex,
                jobId: allocateItem.jobId,
                id: allocateItem.id,
                item: allocateItem.item,
                itemId: allocateItem.itemId,
                originalTotal: totalQty,
                originalTruckId: allocateItem.truckId,
                originalKwikstage: originalKs,
                selectedTrucks: selectedTrucks,
                isKwikstage: originalKs.length > 0,
                isVoid: allocateItem.itemIsVoid,
                formula: formula,
                itemVoidExtraTime: allocateItem.itemVoidExtraTime,
                size: allocateItem.size,
                voidSizeMin: this.state.voidSizeMin,
                scheduleType: allocateItem.typeId,
                extraHours: allocateItem.extraHours,
                multipleTrucks: multipleTrucks
            };

            this.setState({ showMultipleTrucks: true, multipleTruckItem: multipleTrucksItem });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    updateMultipleTrucksData = (multipleTruckItem: MultipleTruckItem) => {
        var multipleTrucksItem = this.state.multipleTruckItem;
        multipleTrucksItem = multipleTruckItem;

        let multipleTrucks = multipleTrucksItem.multipleTrucks;

        for (var i = 0; i < multipleTrucks.length; i++) {
            if (multipleTrucks[i].updateOrder) {
                //find the max order number for this truck and add item to it
                var filteredItems = this.state.allocateItems.filter(data => data.truckId == multipleTrucks[i].truckId);
                if (filteredItems.length > 0) {
                    var lastOrder: number = _.orderBy(filteredItems, ['vehicleItemOrder'], ['desc'])[0].vehicleItemOrder;
                    multipleTrucks[i].vehicleItemOrder = lastOrder + 1;
                } else {
                    multipleTrucks[i].vehicleItemOrder = 1;
                }
            }
        }

        this.setState({ multipleTruckItem: multipleTrucksItem });
    }

    saveMultipleTrucks = () => {
        this.saveMultiTrucks();
    }

    async saveMultiTrucks() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let multipleTrucks = this.state.multipleTruckItem.multipleTrucks;

        let multipleTruckSave = {
            id: this.state.multipleTruckItem.id,
            multipleTrucks: multipleTrucks,
            subId: user.sub
        };

        axios.post('api/Allocate/SaveMultipleTruckAllocation', multipleTruckSave, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Item Saved Successfully");
            var multipleTruckItem = this.resetMultipleTruckData();

            this.setState({ multipleTruckItem: multipleTruckItem, showMultipleTrucks: false, errors: {} });

            this.getScheduledItems();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideMultipleTrucksModal = () => {
        var multipleTruckItem = this.resetMultipleTruckData();
        this.setState({ showMultipleTrucks: false, multipleTruckItem: multipleTruckItem });
    }

    resetMultipleTruckData = () => {
        var multipleTruckItem: MultipleTruckItem = {
            rowIndex: 0,
            jobId: "",
            id: 0,
            item: "",
            itemId: -1,
            originalTotal: 0,
            originalKwikstage: [],
            originalTruckId: -1,
            formula: 0,
            multipleTrucks: [],
            selectedTrucks: [],
            isKwikstage: false,
            isVoid: false,
            itemVoidExtraTime: 0,
            size: 0,
            voidSizeMin: 0,
            scheduleType: -1,
            extraHours: 0
        };
        return multipleTruckItem;
    }

    //simple
    allocateMultiTrucksSimple = (e: any, itemIndex: number) => {
        e.preventDefault();

        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        this.setState({ showMultipleTrucksSimple: true, scheduleId: allocateItem.id, jobQuoteItemId: allocateItem.jobQuoteItemId });
    }

    hideMultipleTrucksSimple = () => {
        this.setState({ showMultipleTrucksSimple: false, scheduleId: -1, jobQuoteItemId: -1 });
    }

    saveMultipleTrucksSimple = () => {
        this.setState({ showMultipleTrucksSimple: false, scheduleId: -1, jobQuoteItemId: -1 });
        this.getScheduledItems();
    }
    //end simple

    //End Allocate Multiple Trucks

    //SEND TRUCKS TO ASSIST
    sendTrucksToAssist = (e: any, itemIndex: number) => {
        e.preventDefault();

        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        if (!allocateItem.truckId) {
            toast.error("You must allocate a truck before sending trucks out to assist!");
        } else {
            this.getTruckAssistJobs(allocateItem);
        }
    }

    async getTruckAssistJobs(allocateItem: AllocateItem) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //get list of jobs with the same Job Id and Truck and Linked Jobs with the same Truck
        axios.get("api/Allocate/GetTruckAssistJobs?ScheduleId=" + allocateItem.id + "&ScheduleDate=" + this.props.scheduleDate + '&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var availTrucks = this.state.systemCompanyId === 3 ? this.state.truckOptions : res.data.availableTrucksOptions;
            var jobs = res.data.jobs;

            this.setState({ truckAssistScheduleId: allocateItem.id, truckAssistTruckId: allocateItem.truckId, truckAssistTruck: allocateItem.truck, truckAssistSiteAddress: allocateItem.siteAddress, availTruckOptions: availTrucks, truckAssistJobs: jobs, trucksAssisting: [], showTruckAssist: true });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideTruckAssist = () => {
        this.setState({ truckAssistScheduleId: 0, truckAssistTruckId: -1, truckAssistTruck: "", truckAssistSiteAddress: "", truckAssistJobs: [], trucksAssisting: [], availTruckOptions: this.state.truckOptions, showTruckAssist: false});
    }

    updateTruckAssistData = (truckAssistJobs: AssistTruckScheduleJob[]) => {
        this.setState({ truckAssistJobs: truckAssistJobs});
    }

    updateAvailTruckOptions = (availTruckOptions: TruckListOption[]) => {
        this.setState({ availTruckOptions: availTruckOptions });
    }

    updateTrucksAssisting = (trucksAssisting: TruckListOption[]) => {
        this.setState({ trucksAssisting: trucksAssisting });
    }

    saveTruckAssist = () => {
        this.saveTruckAssistJobs();
    }

    async saveTruckAssistJobs() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckAssistJobs = this.state.truckAssistJobs.filter(j => j.selected);   //only send trucks to the jobs that have been selected

        var finalAssistJobs = {
            scheduleDate: this.props.scheduleDate,
            truckAssistJobs: truckAssistJobs,
            trucksAssisting: this.state.trucksAssisting,
            subId: user.sub
        };

        axios.post('api/Allocate/SaveTruckAssistJobs', finalAssistJobs, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                this.setState({ truckAssistScheduleId: 0, truckAssistTruckId: -1, truckAssistTruck: "", truckAssistSiteAddress: "", truckAssistJobs: [], trucksAssisting: [], availTruckOptions: this.state.truckOptions, showTruckAssist: false });
                //get scheduled items
                this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
                toast.success("Allocation Truck Assistance Saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }
    //END SEND TRUCKS TO ASSIST

    //COPY
    copyItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        //check if any items have changed
        let itemsChanged = this.state.allocateItems.filter(a => a.isDirty);
        if (itemsChanged.length > 0) {
            if (this.validate()) {
                this.saveData(false);
                this.showCopyItem(itemIndex);
            }
        } else {
            this.showCopyItem(itemIndex);
        }
    }

    showCopyItem = (itemIndex: number) => {
        var itemsToCopy = this.state.allocateItems;
        var itemToCopy = itemsToCopy[itemIndex];

        if (itemToCopy.itemIsKwikstage) {
            this.getKwikstageRollover(itemIndex, itemToCopy);
        } else {
            var copyDetails: CopyScheduleItem = {
                index: itemIndex,
                scheduleId: itemToCopy.id,
                siteAddress: itemToCopy.siteAddress,
                item: itemToCopy.item,
                quantityScheduled: itemToCopy.quantityScheduled,
                qtyType: itemToCopy.quantityType,
                unitOfMeasure: itemToCopy.unitOfMeasure,
                isKwikstage: itemToCopy.itemIsKwikstage,
                kwikstageItems: [],
                kwikstageOptions: []
            };

            let jobs: JobItem[] = [];
            let jobItem: JobItem = {
                itemIndex: 0,
                quantity: 0,
                scheduleDate: "",
                warning: false,
                locked: false
            };
            jobs.push(jobItem);

            let onOffhireDate = itemToCopy.typeId == 1 ? itemToCopy.onhireDate : itemToCopy.offhireDate;
            this.setState({ onOffhireDate: onOffhireDate, jobs: jobs, kwikstageJobs: [], showCopy: true, copyItem: copyDetails });
        }   
    }

    async getKwikstageRollover(itemIndex: number, itemToCopy: AllocateItem) {
        const token = await authService.getAccessToken();

        //need to get some extra details for kwikstage items
        axios.get("api/Allocate/GetKwikstageRolloverDetails?ScheduleId=" + itemToCopy.id, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var ksItems = res.data.kwikstageRolloverItems;
            var ksOptions = res.data.kwikstageOptions;
            var totalBays = res.data.totalBays;

            var copyDetails: CopyScheduleItem = {
                index: itemIndex,
                scheduleId: itemToCopy.id,
                siteAddress: itemToCopy.siteAddress,
                item: itemToCopy.item,
                quantityScheduled: totalBays,
                qtyType: itemToCopy.quantityType,
                unitOfMeasure: itemToCopy.unitOfMeasure,
                isKwikstage: itemToCopy.itemIsKwikstage,
                kwikstageItems: ksItems,
                kwikstageOptions: ksOptions
            };

            let onOffhireDate = itemToCopy.typeId == 1 ? itemToCopy.onhireDate : itemToCopy.offhireDate;
            this.setState({ onOffhireDate: onOffhireDate, kwikstageJobs: res.data.kwikstageJobs, jobs: [], showCopy: true, copyItem: copyDetails });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideCopy = () => {
        var copyDetails = this.resetCopyItem();
        this.setState({ showCopy: false, onOffhireDate: "", copyItem: copyDetails });
    }

    resetCopyItem = () => {
        var item: CopyScheduleItem = {
            index: -1,
            scheduleId: 0,
            siteAddress: "",
            item: "",
            quantityScheduled: 0,
            qtyType: "",
            unitOfMeasure: "",
            isKwikstage: false,
            kwikstageItems: [],
            kwikstageOptions: []
        };
        return item;
    }

    updateOnOffHireDate = (onOffHireDate: string) => {
        this.setState({ onOffhireDate: onOffHireDate });
    }

    updateCopyJobs = (jobs: JobItem[]) => {
        this.setState({ jobs: jobs });
    }

    updateCopyKwikstageJobs = (kwikstageJobs: KwikstageJobItem[]) => {
        this.setState({ kwikstageJobs: kwikstageJobs });
    }

    saveCopyItem = (saveItem: SaveItem) => {
        var copyDetails = this.resetCopyItem();
        this.setState({ onOffhireDate: "", showCopy: false, copyItem: copyDetails });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
    }
    //END COPY

    //PLACEHOLDER JOB
    createPlaceholder = (e: any, itemIndex: number) => {
        e.preventDefault();

        var jobs = this.state.allocateItems;
        var job = jobs[itemIndex];

        let newScheduleDate = DateHelpers.addBusinessDaysToDate(job.scheduleDate, 1);
        let onOffhireDate = job.typeId === 1 ? DateHelpers.addDaysToDate(newScheduleDate, 1) : job.offhireDate;

        this.setState({ showPlaceholderModal: true, placeHolderScheduleDate: newScheduleDate, onOffhireDate: onOffhireDate, jobIndex: itemIndex });
    }

    hidePlaceholder = () => {
        this.setState({ showPlaceholderModal: false, placeHolderScheduleDate: "", onOffhireDate: "", jobIndex: -1 });
    }

    updatePlaceholder = (updatedScheduleDate: string, updatedOnOffhireDate: string) => {
        this.setState({ placeHolderScheduleDate: updatedScheduleDate, onOffhireDate: updatedOnOffhireDate });
    }

    savePlaceholder = () => {
        this.savePlaceholderJob();
    }

    async savePlaceholderJob() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var jobs = this.state.allocateItems;
        var job = jobs[this.state.jobIndex];

        var placeholderJob = {
            scheduleId: job.id,
            scheduleDate: this.state.placeHolderScheduleDate,
            onOffHireDate: this.state.onOffhireDate,
            subId: user.sub
        };

        axios.post('api/Allocate/SavePlaceHolderJob', placeholderJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Placeholder job created successfully");
                this.setState({ showPlaceholderModal: false, placeHolderScheduleDate: "", onOffhireDate: "", jobIndex: -1 });
                this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END PLACEHOLDER JOB

    //CHANGE BRANCH
    changeBranch = (e: any, itemIndex: number) => {
        e.preventDefault();

        this.setState({ showChangeBranch: true, branchId: -1, jobIndex: itemIndex });
    }

    moveBranchSelected = () => {
        var selectedJobs = this.state.allocateItems.filter(a => a.selected);
        if (selectedJobs.length === 0) {
            toast.error("Select one or more jobs before moving to another branch!");
        } else {
            this.setState({ showChangeBranch: true, branchId: -1, selectedJobs: true });
        }
    }

    hideChangeBranch = () => {
        this.setState({ showChangeBranch: false, branchId: -1, jobIndex: -1, selectedJobs: false });
    }

    updateChangeBranch = (branchId: number) => {
        this.setState({ branchId: branchId });
    }

    saveChangeBranch = () => {
        if (this.state.branchId != this.state.branchFilterId) {
            if (this.state.jobIndex >= 0) {
                this.saveBranchChange();
            } else if (this.state.selectedJobs) {
                this.saveMultipleBranchChange();
            } else {
                toast.error("There was an issue moving job/s to another branch");
            }
        } else {
            toast.info("Job is already allocated to this Branch");
            this.setState({ showChangeBranch: false, branchId: -1, jobIndex: -1, selectedJobs: false });
        }
    }

    async saveBranchChange() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var items = this.state.allocateItems;
        var item = items[this.state.jobIndex];

        var changeBranch = {
            id: item.id,
            branchId: this.state.branchId,
            subId: user.sub
        };

        axios.post('api/Allocate/ChangeBranch', changeBranch, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            //remove item from list
            items.splice(this.state.jobIndex, 1);
            //reset itemindex
            for (var i = 0; i < items.length; i++) {
                items[i].itemIndex = i;
            }

            this.setState({ allocateItems: items, showChangeBranch: false, branchId: -1, jobIndex: -1, selectedJobs: false });

            toast.success("Branch has been updated");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async saveMultipleBranchChange() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var selectedItems = this.state.allocateItems.filter(a => a.selected);

        var scheduleIds: number[] = [];
        for (var i = 0; i < selectedItems.length; i++) {
            scheduleIds.push(selectedItems[i].id);
        }

        var changeBranch = {
            ids: scheduleIds,
            branchId: this.state.branchId,
            subId: user.sub
        };

        axios.post('api/Allocate/ChangeBranchMultiple', changeBranch, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ showChangeBranch: false, branchId: -1, jobIndex: -1, selectedJobs: false });
            this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
            toast.success("Branch has been updated");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END MOVE BRANCH

    //SHOW INFO
    showInfo = (e: any, index: number) => {
        e.preventDefault();

        //check if any items have changed
        let itemsChanged = this.state.allocateItems.filter(x => x.isDirty);
        if (itemsChanged.length > 0) {
            if (this.validate()) {
                this.saveData(false);
                this.showInfoModal(index);
            } else {
                toast.error("Please fix validation errors and Save before viewing job info!");
            }
        } else {
            this.showInfoModal(index);
        }
    }

    showInfoModal = (jobIndex: number) => {
        var schedule = this.state.allocateItems[jobIndex];
        this.setState({ showJobInfo: true, scheduleId: schedule.id, jobIndex: jobIndex });
    }

    hideJobInfo = () => {
        this.setState({ showJobInfo: false, scheduleId: -1, jobQuoteItemId: -1, jobIndex: -1 });
    }
    
    openGearlist = () => {
        var schedule = this.state.allocateItems[this.state.jobIndex];
        this.setState({ showJobInfo: false, showVoidGearList: true, jobQuoteItemId: schedule.jobQuoteItemId });
    }

    saveJobInfo = () => {
        toast.success("Job Info Saved");
        this.setState({ showJobInfo: false, scheduleId: -1, jobQuoteItemId: -1, jobIndex: -1 });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
    }
    //END SHOW INFO

    //VOID GEAR LIST
    hideVoidGearList = () => {
        this.setState({ showVoidGearList: false, showJobInfo: true, jobQuoteItemId: -1 });
    }

    saveVoidGearList = () => {
        this.setState({ showVoidGearList: false, showJobInfo: true, jobQuoteItemId: -1 });
    }
    //END VOID GEARLIST

    //SHOW ASSOCIATED JOBS
    showAssociatedJobs = (e: any, jobIndex: number) => {
        e.preventDefault();

        var schedule = this.state.allocateItems[jobIndex];
        this.setState({ showAssociatedItems: true, scheduleId: schedule.id });
    }

    hideAssociatedJobs = () => {
        this.setState({ showAssociatedItems: false, scheduleId: 0 });
    }

    //END SHOW ASSOCIATED JOBS

    //FLAGGED JOBS
    showFlaggedJobs = () => {
        this.setState({ showFlaggedJobs: true});
    }

    hideFlaggedJobs = () => {
        this.setState({ showFlaggedJobs: false });
    }

    //datesToUpdate not used here, just on the Schedule screen
    saveFlaggedJobs = (datesToUpdate: string[]) => {
        this.setState({ showFlaggedJobs: false });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, this.state.truckFilterId, this.state.truckShowUnallocatedFilter, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
        toast.success("Schedule Saved");
    }

    viewScheduleDate = (scheduleDate: string, scheduleId: number) => {
        this.setState({ showFlaggedJobs: false });
        this.props.updateScheduleDate(scheduleDate);
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    //END FLAGGED JOBS

    //DELIVERY ADDRESS
    editDelAddress = (e: any, itemIndex: number) => {
        e.preventDefault();

        var items = this.state.allocateItems;
        var item = items[itemIndex];

        var delAddress: DeliveryAddress = {
            deliveryAddress: item.deliveryAddress,
            deliveryAddressGooglePlaceId: item.deliveryAddressGooglePlaceId,
            deliveryAddressLatitude: item.deliveryAddressLatitude,
            deliveryAddressLongitude: item.deliveryAddressLongitude
        };

        this.setState({ jobIndex: itemIndex, showDelAddress: true, deliveryAddress: delAddress, invoiceDescription: item.invoiceDescription, showInvoiceDescription: item.itemIsEvent, trolley: item.trolley });
    }

    hideDelAddress = () => {
        var delAddress = this.resetDeliveryAddress();
        this.setState({ jobIndex: -1, showDelAddress: false, deliveryAddress: delAddress, invoiceDescription: "", showInvoiceDescription: false, trolley: false });
    }

    updateDelAddress = (updatedDeliveryAddress: DeliveryAddress) => {
        this.setState({ deliveryAddress: updatedDeliveryAddress });
    }

    clearDelAddress = () => {
        var delAddress = this.resetDeliveryAddress();
        this.setState({ deliveryAddress: delAddress });
    }

    updateInvDesc = (updatedInvoiceDescription: string) => {
        this.setState({ invoiceDescription: updatedInvoiceDescription });
    }

    updateTrolley = (updatedTrolley: boolean) => {
        //not used here
    }

    resetDeliveryAddress = () => {
        var delAddress: DeliveryAddress = {
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0
        };

        return delAddress;
    }

    saveDelAddress = () => {
        this.saveDeliveryAddress();
    }

    saveDeliveryAddress = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var items = this.state.allocateItems;
        var item = items[this.state.jobIndex];

        item.siteAddress = this.state.deliveryAddress.deliveryAddress == "" ? item.siteAddressActual : this.state.deliveryAddress.deliveryAddress;
        item.deliveryAddress = this.state.deliveryAddress.deliveryAddress;
            item.deliveryAddressGooglePlaceId = this.state.deliveryAddress.deliveryAddressGooglePlaceId;
        item.deliveryAddressLatitude = this.state.deliveryAddress.deliveryAddressLatitude;
        item.deliveryAddressLongitude = this.state.deliveryAddress.deliveryAddressLongitude;
        item.invoiceDescription = this.state.invoiceDescription;
        item.trolley = this.state.trolley;

        var saveDelAddress = {
            scheduleId: item.id,
            jobQuoteItemId: item.jobQuoteItemId,
            deliveryAddress: this.state.deliveryAddress.deliveryAddress,
            googlePlaceId: this.state.deliveryAddress.deliveryAddressGooglePlaceId,
            deliveryAddressLatitude: this.state.deliveryAddress.deliveryAddressLatitude,
            deliveryAddressLongitude: this.state.deliveryAddress.deliveryAddressLongitude,
            invoiceDescription: this.state.invoiceDescription,
            trolley: this.state.trolley,
            subId: user.sub
        };

        //save
        axios.post('api/Job/SaveDeliveryAddress', saveDelAddress, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Extras Saved");
            var delAddress = this.resetDeliveryAddress();
            item.rowVersionTimestamp = res.data;
            this.setState({ allocateItems: items, jobIndex: -1, scheduleId: -1, showDelAddress: false, deliveryAddress: delAddress, invoiceDescription: "", showInvoiceDescription: false, trolley: false });  
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //END DELIVERY ADDRESS

    //Calculate Kwikstage Popup 
    calculateKwikstage = (e: any, itemIndex: number) => {
        e.preventDefault();

        this.getKwikstageBays(itemIndex);
    }

    async getKwikstageBays(itemIndex: number) {
        const token = await authService.getAccessToken();
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        //get any calculator items
        axios.get("api/Allocate/GetKwikstageBays?ScheduleId=" + allocateItem.id + "&JobQuoteItemId=" + allocateItem.jobQuoteItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        }).then(res => {
            var ksItem = res.data.ksItem;
            var items = res.data.calculatorBays;
            
            this.setState({ showCalculator: true, calculatorTypeId: (allocateItem.typeId * 1), calculatorIndex: itemIndex, calculatorStatusId: allocateItem.itemStatusId, calculatorItems: items, calculatorKsItem: ksItem });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    hideCalculateKwikstage = () => {
        let ksItem: KwikstageItem = {
            kwikstageInstalledFor: -1,
            extraHours: 0,
            quoteNote: ""
        };
        this.setState({ showCalculator: false, calculatorIndex: -1, calculatorStatusId: -1, calculatorItems: [], calculatorKsItem: ksItem });
    }

    updateCalculateKwikstage = (items: KwikstageCalculation[]) => {
        this.setState({ calculatorItems: items });
    }

    updateKwikstageItem = (item: KwikstageItem) => {
        //not used on this screen
    }

    saveCalculateKwikstage = () => {
        this.saveKwikstageBays();
    }

    async saveKwikstageBays() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[this.state.calculatorIndex];

        var calulatorItems = this.state.calculatorItems;

        var saveBays = {
            jobId: allocateItem.jobId,
            id: allocateItem.id,
            itemFormula: allocateItem.typeId === 1 ? allocateItem.itemInstallFormula : allocateItem.itemPickupFormula,
            calculatorBays: calulatorItems,
            subId: user.sub
        }

        axios.post('api/Allocate/SaveKwikstageBays', saveBays, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            allocateItem.quantityScheduled = res.data.quantityScheduled;
            allocateItem.quantityType = res.data.quantityType;
            allocateItem.bays = res.data.bays;
            allocateItem.sizeBays = res.data.sizeBays;
            allocateItem.estimatedTime = res.data.estimatedTime;
            allocateItem.rowVersionTimestamp = res.data.rowVersionTimestamp;
            let ksItem: KwikstageItem = {
                kwikstageInstalledFor: -1,
                extraHours: 0,
                quoteNote: ""
            };

            this.setState({ allocateItems: allocateItems, showCalculator: false, calculatorIndex: -1, calculatorStatusId: -1, calculatorItems: [], calculatorKsItem: ksItem });

            toast.success("Kwikstage Bays have been updated");
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //end calculate kwikstage

    //grid item changed
    onCellChange = (itemIndex: number, item: string, value: any) => {
        //update item that has changed
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];
        let newAllocateItem: any = {};
        newAllocateItem = allocateItem;
        newAllocateItem[item] = value;
        newAllocateItem.isDirty = true;

        //if quantity has changed then recalculate estimated time
        if (item == "quantityScheduled" && allocateItem.truckId > 0) {
            //calculate estimated time
            var estTime = "";
            if (allocateItem.itemIsVoid) {
                estTime = Utils.calculateVoidEstimatedTime(true, value, allocateItem.size, allocateItem.numberTeamMembers, this.state.voidSizeMin, allocateItem.itemVoidFormula, allocateItem.itemVoidExtraTime, allocateItem.typeId, allocateItem.extraHours);
            } else {
                var formulaAmt = allocateItem.typeId === 1 ? allocateItem.itemInstallFormula : allocateItem.itemPickupFormula;
                if (formulaAmt > 0) {
                    //not all items have a formula calculation
                    estTime = Utils.calculateEstimatedTime(value, allocateItem.numberTeamMembers, formulaAmt, allocateItem.typeId, allocateItem.extraHours);
                }
            }
            allocateItem.estimatedTime = estTime;
        }

        this.setState({ allocateItems: allocateItems, errors: {} });
    }

    selectAllUnallocated = (e: any) => {
        var selected = e.target.checked;

        //need to select all jobs that are not allocated to a truck
        var allocateItems = this.state.allocateItems;
        for (var i = 0; i < allocateItems.length; i++) {
            let allocItem = allocateItems[i];
            if (allocItem.truckId < 1) {
                allocItem.selected = selected;
            }
        }
        this.setState({ allocateItems: allocateItems, selectAll: selected });
    }

    selectAllPrintedJobs = (e: any) => {
        var selected = e.target.checked;

        //need to select all jobs that are not allocated to a truck
        var allocateItems = this.state.allocateItems;
        for (var i = 0; i < allocateItems.length; i++) {
            allocateItems[i].printed = selected;
            allocateItems[i].isDirty = true;
        }
        this.setState({ allocateItems: allocateItems, selectAllPrinted: selected });
    }

    onCellCheckboxChange = (itemIndex: number, e: any) => {
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];

        let newAllocateItem: any = {};
        newAllocateItem = allocateItem;
        newAllocateItem[e.target.name] = e.target.checked;
        newAllocateItem.isDirty = true;
        this.setState({ allocateItems: allocateItems });
    }

    //Schedule Date has changed so clear truck and driver team
    onScheduleDateCellChange = (itemIndex: number, item: string, value: any) => {
        this.checkLockedWarning(itemIndex, item, value);
    }

    async checkLockedWarning(itemIndex: number, item: string, value: any) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        //update item that has changed
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];
        let newAllocateItem: any = {};
        newAllocateItem = allocateItem;
        newAllocateItem[item] = value;
        newAllocateItem.isDirty = true;

        //clear items unless date is in the past
        if (!DateHelpers.isDateInPast(value)) {
            allocateItem.vehicleItemOrder = 0;
            allocateItem.teamDisplay = "";
            allocateItem.truck = "";
            allocateItem.truckId = 0;
        }

        if (this.state.systemCompanyId === 3) {
            //company 3 doesn't use the schedule screen
            allocateItem.locked = false;
            allocateItem.warning = false;
            this.setState({ allocateItems: allocateItems, errors: {} });
        } else {
            //check to see if the date is locked or a public holiday or within a shutdown period
            axios.get('api/Schedule/CheckDateLockedWarning?ScheduleDate=' + value + '&ScheduleBranchId=' + allocateItem.scheduleBranchId + '&SubId=' + user.sub, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                var locked = res.data.locked;
                var holiday = res.data.warning;

                if (locked) {
                    allocateItem.locked = true;
                    allocateItem.warning = false;
                    this.setState({ allocateItems: allocateItems, errors: {} });
                    toast.error("The date you have selected has been locked, please select a new date or unlock before rescheduling!");
                } else if (holiday) {
                    allocateItem.locked = false;
                    allocateItem.warning = true;
                    this.setState({ allocateItems: allocateItems, errors: {} });
                } else {
                    allocateItem.locked = false;
                    allocateItem.warning = false;
                    this.setState({ allocateItems: allocateItems, errors: {} });
                };
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
        
    }

    //truck dropdown in the grid changed
    onTruckCellChange = (itemIndex: number, e: any) => {
        //update item that has changed
        var allocateItems = this.state.allocateItems;
        var allocateItem = allocateItems[itemIndex];
        var oldTruckId = allocateItem.truckId;
        var oldOrder = allocateItem.vehicleItemOrder;

        //find the max order number for this truck and add item to it
        var filteredItems = allocateItems.filter(data => data.truckId == e.target.value);
        if (filteredItems.length > 0) {
            
            var lastOrder: number = _.orderBy(filteredItems, ['vehicleItemOrder'], ['desc'])[0].vehicleItemOrder;
            allocateItem["vehicleItemOrder"] = lastOrder + 1;
        } else {
            allocateItem["vehicleItemOrder"] = 1;
        }

        var truck = this.state.truckOptions.filter(data => data.id == e.target.value);

        //set the new truck id
        allocateItem["truckId"] = e.target.value;
        allocateItem["truck"] = e.target.selectedOptions[0].label;
        allocateItem["teamDisplay"] = truck.length === 0 ? "" : truck[0].teamDisplay;
        allocateItem["numberTeamMembers"] = truck.length === 0 ? 0 : truck[0].numberTeamMembers;
        allocateItem["isDirty"] = true;
        allocateItem["printed"] = false;

        //calculate estimated time
        if (e.target.value <= 0) {
            allocateItem.estimatedTime = "";
        } else {
            var estTime = "";
            if (allocateItem.isPlaceholder && allocateItem.quantityScheduled === 0) {
                estTime = "0:01";
            } else if (allocateItem.itemIsVoid) {
                estTime = Utils.calculateVoidEstimatedTime(true, allocateItem.quantityScheduled, allocateItem.size, allocateItem.numberTeamMembers, this.state.voidSizeMin, allocateItem.itemVoidFormula, allocateItem.itemVoidExtraTime, allocateItem.typeId, allocateItem.extraHours);
            } else {
                var formulaAmt = allocateItem.typeId === 1 ? allocateItem.itemInstallFormula : allocateItem.itemPickupFormula;
                if (formulaAmt > 0) {
                    //not all items have a formula calculation
                    estTime = Utils.calculateEstimatedTime(allocateItem.quantityScheduled, truck[0].numberTeamMembers, formulaAmt, allocateItem.typeId, allocateItem.extraHours);
                }
            }
            allocateItem.estimatedTime = estTime;
        }
        //if we have changed from one truck to another truck then need to reorder the old truck
        if (allocateItem.truckId > 0) {
            var reOrderItems = allocateItems.filter(data => data.truckId == oldTruckId && data.vehicleItemOrder > oldOrder);
            for (var i = 0; i < reOrderItems.length; i++) {
                reOrderItems[i].vehicleItemOrder = reOrderItems[i].vehicleItemOrder <= 1 ? 1 : reOrderItems[i].vehicleItemOrder - 1;
            }
        }
        
        this.saveTruckId(allocateItems, allocateItem)
    }

    saveTruckId = async(allocateItems: AllocateItem[], allocationItem: AllocateItem) => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckId = allocationItem.truckId && allocationItem.truckId > 0 ? allocationItem.truckId : -1;

        var saveTruck = {
            id: allocationItem.id,
            truckId: truckId,
            truckOrder: allocationItem.vehicleItemOrder,
            truckName: allocationItem.truck,
            estimatedTime: allocationItem.estimatedTime,
            subId: user.sub
        };

        //save
        axios.post('api/Allocate/SaveTruckToJob', saveTruck, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var timestamp = res.data;
            allocationItem.rowVersionTimestamp = timestamp;
            this.setState({ allocateItems: allocateItems, errors: {} });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    refresh = () => {
        this.setState({ truckFilterId: -1, sortType: 0, sortDirection: 0 });
        this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, -1, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, 0, 0);
    }

    allocateTrucks = () => {
        this.props.goAllocateTruck(this.state.branchFilterId);
    }

    validate = () => {
        let errors: any = {};
        let formIsValid = true;
        let items = this.state.allocateItems.filter(x => x.isDirty);    //only validate items that have changed as they are the only ones to be saved
        var rowNumber = 0;

        //check if any dates are locked before validating
        var locked = items.filter(i => i.locked);
        if (locked.length > 0) {
            toast.error("You must select dates that are not locked in order to reschedule!");
            return false;
        }

        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < items.length; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (!DateHelpers.isValidDate(items[i].scheduleDate)) {
                newErrorString += " Valid Schedule Date, ";
                error = true;
            }

            //check if user has permission to allocate items to a past date
            if (!this.state.allowAllocateToPastDate) {
                if (DateHelpers.isDateGreater5DaysInPast(items[i].scheduleDate)) {
                    newErrorString += " Schedule Date must be within the last 5 days, ";
                    error = true;
                }
            }

            if (!items[i].manualItem) {
                if (items[i].typeId === 1 && !DateHelpers.isValidDate(items[i].onhireDate)) {
                    newErrorString += " Valid Onhire Date, ";
                    error = true;
                } else if (items[i].typeId === 1 && !DateHelpers.compareDate(items[i].scheduleDate, items[i].onhireDate)) {
                    newErrorString += " Onhire Date >= Schedule Date, ";
                    error = true;
                }
                if (items[i].typeId === 2 && !DateHelpers.isValidDate(items[i].offhireDate)) {
                    newErrorString += " Valid Offhire Date, ";
                    error = true;
                } else if (items[i].typeId === 2 && !DateHelpers.compareDate(items[i].offhireDate, items[i].scheduleDate)) {
                    newErrorString += " Offhire Date <= Schedule Date, ";
                    error = true;
                }
            }

            if (items[i].quantityTotal < 1) {
                newErrorString += "Quantity Total, ";
                error = true;
            }
            if (items[i].quantityScheduled > items[i].quantityTotal) {
                newErrorString += " Quantity must be <= Quantity Total, ";
                error = true;
            }
            //quantity can be 0 for trucks assisting (truckAssistScheduleId > 0)
            if (items[i].truckAssistScheduleId === 0 && items[i].quantityScheduled < 1 && !items[i].isPlaceholder) {
                newErrorString += "Quantity, ";
                error = true;
            }

            if (this.state.systemCompanyId !== 3) {
                if (items[i].truckId > 0 && (items[i].estimatedTime == "" || items[i].estimatedTime == "0")) {
                    newErrorString += "Estimated Time, ";
                    error = true;
                } else if (items[i].truckId > 0 && !DateHelpers.validTimeAmount(items[i].estimatedTime)) {
                    newErrorString += " Valid Estimated Time, ";
                    error = true;
                }
            }

            if (newErrorString != "") {
                errorString += "\n";
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }
        }

        if (error) {
            formIsValid = false;
            errors["allocatedItems"] = errorString;
        }
        this.setState({ errors: errors });
        return formIsValid;
    }

    submit = (e: any) => {
        e.preventDefault();
        this.saveData(true);
    }

    async saveData(fromSave: boolean) {
        if (this.validate()) {
            //only save items that have changed
            var allocate = this.state.allocateItems.filter(x => x.isDirty);
            var truckFilter = this.state.truckFilterId;

            if (allocate.length > 0) {
                const token = await authService.getAccessToken();
                const user = await authService.getUser();

                var saveAllocate = {
                    items: allocate,
                    subId: user.sub
                };

                //save
                axios.post('api/Allocate/SaveAllocation', saveAllocate, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    if (res.data.isError) {
                        var errorList = this.state.errors;
                        var errors = res.data.messages as any[];
                        var scheduleErrors = "";
                        for (var i = 0; i < errors.length; i++) {
                            toast.error(errors[i].content);
                            if (scheduleErrors != "") {
                                scheduleErrors += "\n";
                            }
                            scheduleErrors += errors[i].content;
                        };
                        if (scheduleErrors.length > 2) {
                            scheduleErrors = scheduleErrors.substring(0, scheduleErrors.length - 1);
                            errorList["allocatedItems"] = scheduleErrors;
                        }

                        this.setState({ errors: errorList });
                    } else {
                        if (fromSave) {
                            //only get the items if we are saving the data from the Save button
                            //also save data when changing filters so do not want to show messages or reset data
                            //get scheduled items
                            this.getAllocateItems(this.state.branchFilterId, this.props.itemTypeGroupFilterId, truckFilter, false, this.props.scheduleDate, this.state.showPrinted, this.state.showPending, this.state.sortType, this.state.sortDirection);
                            toast.success("Allocation Schedule Saved");
                        }
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else if (fromSave) {
                toast.info("No items have changed!");
            }
        } else {
            toast.error("Please fix validation errors and try Saving again!");
        }
    }

    printRunSheetSummary = () => {
        this.printSummary();
    }

    async printSummary() {
        //run the report and export the data 
        var reportName = this.state.systemCompanyId === 3 ? "RunsheetContractorSummary" : "RunsheetSummary";

        var url = "/report/" + reportName + "/" + this.props.scheduleDate + "/-1/" + this.state.systemCompanyId + "/" + this.props.itemTypeGroupFilterId + "/" + this.state.truckFilterId;
        window.open(url, '_blank');
    }

    printRunSheets = () => {
        //validate data before printing runsheets
        //if list is filtered we only want to print that list so only validate that list
        //ignore any items that cannot be updated
        if (this.validate()) {
            this.printRunsheet(false);
        } else {
            toast.error("Please fix validation errors, Save and try Printing Runsheets again!");
        }
    }

    printRunSheetsPrinted = () => {
        //validate data before printing runsheets
        //if list is filtered we only want to print that list so only validate that list
        //ignore any items that cannot be updated
        if (this.validate()) {
            this.printRunsheet(true);
        } else {
            toast.error("Please fix validation errors, Save and try Printing Runsheets again!");
        }
    }

    async printRunsheet(printed: boolean) {
        let allocateItems = this.state.truckFilterId > 0 ? this.state.filteredItems : this.state.allocateItems;
        let totalJobs = allocateItems.length;
        let totalCleaningJobs = allocateItems.filter(a => a.typeId === 4).length;
        var isCleaning = totalJobs === totalCleaningJobs && totalCleaningJobs > 0;

        //run the report and export the data 
        var reportName = this.state.systemCompanyId === 3 && printed && isCleaning ? "RunsheetCleaningPrinted" : isCleaning ? "RunsheetCleaning" : this.state.systemCompanyId === 3 ? printed ? "RunsheetContractorPrinted" : "RunsheetContractor" : "Runsheet";

        var url = "/report/" + reportName + "/" + this.props.scheduleDate + "/-1/" + this.state.systemCompanyId + "/" + this.props.itemTypeGroupFilterId + "/" + this.state.truckFilterId;
        window.open(url, '_blank');
    }

    showJobColourKey = (e: any, visible: boolean) => {
        e.preventDefault();

        this.setState({ showColourKey: visible });
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var minDate = this.state.allowAllocateToPastDate ? DateHelpers.addDaysToCurrentDate(-90) : (this.props.scheduleDate < DateHelpers.addDaysToCurrentDate(-5)) ? this.props.scheduleDate : DateHelpers.addDaysToCurrentDate(-5);

        let allocateItems = this.state.truckFilterId > 0 ? this.state.filteredItems : this.state.allocateItems;
        let tooltipBottomRows = allocateItems.length > 5 ? allocateItems.length - 3 : allocateItems.length;
        let truckOptions = this.state.branchFilterId > 0 ? this.state.filteredTrucks : this.state.truckOptions;
        let scheduleBranchOptions = this.state.scheduleBranchOptions;

        var cancelModal = <AllocateCancelModal cancelDetails={this.state.cancelDetails} cancelOptions={this.state.cancelOptions} showModal={this.state.showCancelModal} hideModal={this.hideCancelModal} updateData={this.updateCancelData} save={this.saveCancel} />
        var cancelMultiModal = <AllocateCancelMultiModal cancelDetails={this.state.cancelMultiDetails} showModal={this.state.showMultiCancelModal} hideModal={this.hideCancelMultiModal} allocateExisting={this.allocateCancelItem} />
        var extendModal = <AllocateExtendModal scheduleId={this.state.scheduleId} jobQuoteItemId={this.state.jobQuoteItemId} showExtendModal={this.state.showExtendModal} hideModal={this.hideExtendModal} save={this.saveExtendData} />
        var addItem = <AllocateAddItemModal systemCompanyId={this.state.systemCompanyId} newItem={this.state.newItem} showNewItemModal={this.state.showNewItemModal} scheduleTypes={this.state.scheduleTypes} itemTypeGroups={this.state.itemTypeGroupOptions} driverJob={false} hideModal={this.hideNewItemModal} updateData={this.updateNewItemData} save={this.saveNewItemData} />
        var editNotes = <EditNoteModal systemCompanyId={this.state.systemCompanyId} scheduleId={this.state.scheduleId} editDriverNotes={false} showDriverScheduleNotes={false} showEditNotesModal={this.state.showEditNotesModal} save={this.saveNotes} hideModal={this.hideEditNotesModal} />
        var multiTrucks = <AllocateMultiTrucksModal multipleTrucksItem={this.state.multipleTruckItem} showModal={this.state.showMultipleTrucks} truckOptions={this.state.truckOptions} updateData={this.updateMultipleTrucksData} hideModal={this.hideMultipleTrucksModal} save={this.saveMultipleTrucks} />
        var multiTrucksSimple = <AllocateMultiTrucksSimpleModal showModal={this.state.showMultipleTrucksSimple} truckOptions={this.state.truckOptions} scheduleId={this.state.scheduleId} jobQuoteItemId={this.state.jobQuoteItemId} scheduleDate={this.props.scheduleDate} hideModal={this.hideMultipleTrucksSimple} save={this.saveMultipleTrucksSimple} />
        var linkedJobs = <AllocateLinkJobsModal showModal={this.state.showLinkJobsModal} siteAddress={this.state.linkedJobSiteAddress} allocatedJobs={this.state.linkedJobs} hideModal={this.hideLinkJobModal} updateSiteAddress={this.updateLinkModalSiteAddress} updateData={this.updateLinkJobModal} save={this.saveLinkJobModal} saveUnlinkJob={this.saveUnlinkJob} />
        var scheduleBranchId = this.state.copyItem.index < 0 ? 0 : allocateItems[this.state.copyItem.index].scheduleBranchId;
        let copyJob = <CopyJobData onOffhireDate={this.state.onOffhireDate} jobs={this.state.jobs} kwikstageJobs={this.state.kwikstageJobs} scheduleBranchId={scheduleBranchId} showModal={this.state.showCopy} hideModal={this.hideCopy} copyItem={this.state.copyItem} updateOnOffHireDate={this.updateOnOffHireDate} updateJobs={this.updateCopyJobs} updateKwikstageJobs={this.updateCopyKwikstageJobs} saveModal={this.saveCopyItem} />
        let placeholderJob = <AllocatePlaceHolderData scheduleDate={this.state.placeHolderScheduleDate} onOffhireDate={this.state.onOffhireDate} showModal={this.state.showPlaceholderModal} hideModal={this.hidePlaceholder} update={this.updatePlaceholder} saveModal={this.savePlaceholder} />
        let assistTrucks = <AllocateAssistTrucksModal scheduleId={this.state.truckAssistScheduleId} truckId={this.state.truckAssistTruckId} truck={this.state.truckAssistTruck} siteAddress={this.state.truckAssistSiteAddress} jobs={this.state.truckAssistJobs} truckOptions={this.state.truckOptions} availTruckOptions={this.state.availTruckOptions} assistingTrucks={this.state.trucksAssisting} showModal={this.state.showTruckAssist} hideModal={this.hideTruckAssist} updateData={this.updateTruckAssistData} updateAvailTruckOptions={this.updateAvailTruckOptions} updateAssistingTrucks={this.updateTrucksAssisting} save={this.saveTruckAssist} />
        let missingItems = <MissingScheduleItemData systemCompanyId={this.state.systemCompanyId} scheduleDate={this.props.scheduleDate} scheduleBranchId={this.state.branchFilterId} doUpdate={this.state.onHoldChanged} show={this.state.showMissingItems} hide={this.hideMissingItems} save={this.saveMissingItems} />
        let calculatorPopup = <JobQuoteCalculatorModal showModal={this.state.showCalculator} starterBayOptions={this.state.starterBayOptions} liftOptions={this.state.liftOptions} kwikstageInstalledForOptions={[]} statusId={this.state.calculatorStatusId} kwikstageItem={this.state.calculatorKsItem} items={this.state.calculatorItems} editOnly={true} canEditBays={this.state.calculatorTypeId === 2} typeId={this.state.calculatorTypeId} hideModal={this.hideCalculateKwikstage} update={this.updateCalculateKwikstage} updateItem={this.updateKwikstageItem} save={this.saveCalculateKwikstage} />
        let changeBranch = <AllocateChangeBranchModal showModal={this.state.showChangeBranch} branchId={this.state.branchId} hide={this.hideChangeBranch} update={this.updateChangeBranch} save={this.saveChangeBranch} />
        let jobInfo = <JobInfoData systemCompanyId={this.state.systemCompanyId} scheduleId={this.state.scheduleId} showModal={this.state.showJobInfo} hideModal={this.hideJobInfo} openGearlist={this.openGearlist} saveModal={this.saveJobInfo} />
        let editVoidGearlist = <JobScheduleVoidModalData showModal={this.state.showVoidGearList} jobQuoteItemId={this.state.jobQuoteItemId} hideModal={this.hideVoidGearList} saveModal={this.saveVoidGearList} />
        let flaggedJobs = <FlaggedJobsData scheduleDate={this.props.scheduleDate} dateFormatted={this.props.scheduleDateFormatted} systemCompanyId={this.state.systemCompanyId} scheduleBranchId={this.state.branchFilterId} showModal={this.state.showFlaggedJobs} hideModal={this.hideFlaggedJobs} save={this.saveFlaggedJobs} filterDate={this.viewScheduleDate} />
        let moveJobs = <AllocateMoveMultipleItemsModal showModal={this.state.showMoveMultipleJobs} moveDetails={this.state.moveDetails} showOnHireDate={this.state.showMoveOnHireDate} showOffHireDate={this.state.showMoveOffHireDate} hideModal={this.hideMoveJobs} update={this.updateMoveJobs} save={this.saveMoveJobs} />
        let confirmOnHold = <ConfirmPopups heading="Place job On Hold" text="This will cancel all existing installs/pickups and place the job on hold. Place job on hold?" showConfirmModal={this.state.showJobOnHold} hideConfirmModal={this.hideJobOnHold} saveConfirmModal={this.saveJobOnHold} />
        let associatedItems = <AllocateAssociatedJobsData showModal={this.state.showAssociatedItems} scheduleId={this.state.scheduleId} hideModal={this.hideAssociatedJobs} />
        let delAddressPopup = <JobQuoteDeliveryAddressData deliveryAddress={this.state.deliveryAddress} invoiceDescription={this.state.invoiceDescription} showDeliveryAddress={true} showInvoiceDescription={this.state.showInvoiceDescription} trolleyRequired={false} showTrolley={false} readOnly={false} showModal={this.state.showDelAddress} hideModal={this.hideDelAddress} updateDeliveryAddress={this.updateDelAddress} clearDeliveryAddress={this.clearDelAddress} updateInvoiceDescription={this.updateInvDesc} updateTrolley={this.updateTrolley} saveModal={this.saveDelAddress} />

        let warnings = allocateItems.filter(r => r.warning || r.locked);

        return (
            <div>
                {cancelModal}
                {cancelMultiModal}
                {editNotes}
                {extendModal}
                {addItem}
                {multiTrucks}
                {multiTrucksSimple}
                {linkedJobs}
                {copyJob}
                {placeholderJob}
                {assistTrucks}
                {calculatorPopup}
                {missingItems}
                {changeBranch}
                {jobInfo}
                {editVoidGearlist}
                {flaggedJobs}
                {moveJobs}
                {confirmOnHold}
                {associatedItems}
                {delAddressPopup}

                <div id="fixedHeight" className="allocate__search--fixed">
                    <div className="allocate__search--fixed-width marginLeft10 marginRight10">
                        <div className="allocate__statusBar">
                            <div>
                                <label className="input-group--inline" htmlFor="scheduleDate">
                                    <span className="label">Date</span>
                                    <input type='date' id="scheduleDate" name="scheduleDate" className='input' value={this.props.scheduleDate} onChange={(e) => this.handleScheduleDateChange(e)}></input>
                                </label>
                                <button className="defaultbutton defaultbutton__xsmall marginRight10" type="button" onClick={this.refresh}>Refresh</button>
                                <label className="" htmlFor="branchFilterId">
                                    <span className="label">Branch</span>
                                    <select className="select" id="branchFilterId" name="branchFilterId" value={this.state.branchFilterId} onChange={(e) => this.handleBranchChange(e)}>
                                        <option hidden defaultValue="-1" value={-1}></option>
                                        {scheduleBranchOptions.map(branch =>
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className={this.state.systemCompanyId === 3 ? "marginLeft10" : "hidden"} htmlFor="itemTypeGroupFilterId">
                                    <span className="label">Item Type</span>
                                    <select className="select" id="itemTypeGroupFilterId" name="itemTypeGroupFilterId" value={this.props.itemTypeGroupFilterId} onChange={(e) => this.handleItemTypeGroupChange(e)}>
                                        <option hidden defaultValue="-1" value={-1}></option>
                                        {this.state.itemTypeGroupOptions.map(group =>
                                            <option key={group.id} value={group.id}>{group.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="truckFilterId">
                                    <span className="label">Truck</span>
                                    <select className="select" id="truckFilterId" name="truckFilterId" value={this.state.truckFilterId} onChange={(e) => this.handleTruckChange(e)}>
                                        <option defaultValue="-1" value={-1}></option>
                                        {truckOptions.map(truck =>
                                            <option key={truck.id} value={truck.id}>{truck.name}-{truck.registrationNumber}</option>
                                        )};
                                    </select>
                                </label>
                                <label className={this.state.truckFilterId > 0 ? "input-group--inline" : "hidden"} htmlFor="truckShowUnallocatedFilter">
                                    <span className="label label--setsizesm">Inc Unallocated</span>
                                    <input className="input" type="checkbox" name="truckShowUnallocatedFilter" checked={this.state.truckShowUnallocatedFilter} onChange={(e) => this.handleShowUnallocatedChange(e)}></input>
                                </label>
                            </div>
                            <div className="margin10">
                                <button className={this.state.systemCompanyId === 3 && this.state.canEdit ? "defaultbutton defaultbutton__xsmall  allocate__align-top" : this.state.canEdit ? "defaultbutton defaultbutton__medium" : "hidden"} type='button' onClick={this.allocateTrucks}>Allocate Trucks</button>
                                <button className={this.state.systemCompanyId === 3 && this.state.canViewMissingOnHoldJobs ? "defaultbutton defaultbutton__xsmall marginLeft10 allocate__align-top " + (this.state.missingItemCount > 0 ? "makeitred" : "") : "hidden"} type='button' onClick={this.scheduleMissingItems}>Missing {this.state.missingItemCount} <br></br>On Hold {this.state.onHoldItemCount}</button>
                                <button className={this.state.canAddManualJob ? "defaultbutton defaultbutton__xsmall marginLeft10" : "hidden"} type='button' onClick={this.openNewItemModal}>Add New</button>
                                <button className={this.state.systemCompanyId === 3 && this.state.canAddFlaggedJobs ? "defaultbutton defaultbutton__xsmall marginLeft10" : "hidden"} type='button' onClick={this.showFlaggedJobs}>Flagged</button>
                                <button className={this.state.systemCompanyId === 3 ? "hidden" : this.state.canEdit ? "defaultbutton defaultbutton__xsmall marginLeft10" : "hidden"} type='button' onClick={this.openLinkJobModal}>Link Jobs</button>
                                <button className={this.state.canEdit ? "defaultbutton defaultbutton__xsmall marginLeft10" : "hidden"} type="button" onClick={this.submit}>Save</button>
                            </div>
                        </div>
                        <div className="makeitflexspacebetween">
                            <div>
                                <div className="marginLeft10 noteSize">* Items in red are awaiting payment or go ahead sign-off form and cannot be allocated.</div>
                                <div className={this.state.systemCompanyId === 3 ? "marginLeft10 noteSize" : "hidden"}>* Red Job Info button requires a Purchase Order Number</div>
                            </div>
                            <div className={this.state.systemCompanyId === 3 ? "alignitemscentre" : "hidden"}>
                                <span className="marginRight10">Show Pending/Locked Jobs</span>
                                <input className="checkbox" type="checkbox" id="showPending" name="showPending" checked={this.state.showPending} onChange={(e) => this.handleShowPendingChange(e)}></input>
                            </div>
                            <div className={this.state.systemCompanyId === 3 ? "alignitemscentre" : "hidden"}>
                                <span className="marginRight10">Show Printed Jobs</span>
                                <input className="checkbox" type="checkbox" id="showPrinted" name="showPrinted" checked={this.state.showPrinted} onChange={(e) => this.handleShowPrintedChange(e)}></input>
                            </div>
			                <div className={this.state.systemCompanyId === 3 && this.state.canEdit ? "" : "hidden"}>
                                <label className="marginRight10" htmlFor="scheduleDate">
                                    <span className="label">Next Available Schedule Date (days)</span>
                                    <input type='number' step={1} min={0} id="defaultDays" name="defaultDays" className='input input--small marginRight10' value={this.state.defaultDays} onChange={(e) => this.handleDaysChange(e)} disabled={!this.state.allowChangeDefaultDays}></input>
                                    {this.state.nextScheduleDate}
                                </label>
                            </div>
                        </div>
                        <div className="makeitflexspacebetween">
                            <div>
                                <div className="marginLeft10 allocate__customerNoteText">* Items in dark red need Customer unlocked.</div>
                                <div className="marginLeft10 allocate__customerReckonNoteText">* Items in blue need to be linked to Reckon.</div>
                            </div>
                            <div className={this.state.unallocatedItems.length > 0 ? "makeitbold makeitred alignitemscentre marginRight10" : "hidden"}>
                                <span>Unallocated Jobs:</span>
                                {this.state.unallocatedItems.map((item, index) =>
                                    <span key={index} className="marginLeft10">{item.scheduleBranchId} {item.itemTypeGroup === "" ? "" : " - "} {item.itemTypeGroup} - {item.totalJobs}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="allocate__topMargin">
                    <div className="table__margin table__minHeight tableBorder overflowAuto">
                        <table className="table table__extralarge tableColours-header">
                            <thead>
                                <tr>
                                    <th className="hidden">Id</th>
                                    <th></th>
                                    <th className="table--col-xs"></th>
                                    <th className={this.state.canEdit ? "" : "hidden"}>
                                        <input type='checkbox' id="selectAll" name="selectAll" className="marginRight10" checked={this.state.selectAll} onChange={(e) => this.selectAllUnallocated(e)} />
                                        <a className="makeitgreen tooltipitem tooltipitem__right" href="#" onClick={(e) => this.moveJobs(e)}>
                                            <span className="tooltipitemtext tooltipitemtext__right">Select multiple jobs to move to another day (then click this button)</span>
                                            <span className="fas fa-circle-right edit--icon"></span>
                                        </a>
                                    </th>
                                    <th>{this.state.systemCompanyId === 3 ? "Orig" : ""} Date
                                        <i className={this.state.sortType === 4 && this.state.sortDirection === 1 ? "marginLeft10 fa-solid fa-circle-up" : "hidden"} title="Sort Descending" onClick={(e) => this.handleSort(e, 4, 2)}></i>
                                        <i className={(this.state.sortType === 4 && this.state.sortDirection === 2) || (this.state.sortType != 4) ? "marginLeft10 fa-solid fa-circle-down" : "hidden"} title="Sort Ascending" onClick={(e) => this.handleSort(e, 4, 1)}></i>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>On/Offhire</th>
                                    <th>Files</th>
                                    <th className={this.state.systemCompanyId === 3 ? "" : "hidden"}>Customer
                                        <i className={this.state.sortType === 1 && this.state.sortDirection === 1 ? "marginLeft10 fa-solid fa-circle-up" : "hidden"} title="Sort Descending" onClick={(e) => this.handleSort(e, 1, 2)}></i>
                                        <i className={(this.state.sortType === 1 && this.state.sortDirection === 2) || (this.state.sortType != 1) ? "marginLeft10 fa-solid fa-circle-down" : "hidden"} title="Sort Ascending" onClick={(e) => this.handleSort(e, 1, 1)}></i>
                                    </th>
                                    <th>Job Id</th>
                                    <th>Site Address</th>
                                    <th className={this.state.systemCompanyId === 3 ? "" : "hidden"}>Suburb
                                        <i className={this.state.sortType === 2 && this.state.sortDirection === 1 ? "marginLeft10 fa-solid fa-circle-up" : "hidden"} title="Sort Descending" onClick={(e) => this.handleSort(e, 2, 2)}></i>
                                        <i className={(this.state.sortType === 2 && this.state.sortDirection === 2) || (this.state.sortType != 2) ? "marginLeft10 fa-solid fa-circle-down" : "hidden"} title="Sort Ascending" onClick={(e) => this.handleSort(e, 2, 1)}></i>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Link Job</th>
                                    <th>Item
                                        <i className={this.state.sortType === 3 && this.state.sortDirection === 1 ? "marginLeft10 fa-solid fa-circle-up" : "hidden"} title="Sort Descending" onClick={(e) => this.handleSort(e, 3, 2)}></i>
                                        <i className={(this.state.sortType === 3 && this.state.sortDirection === 2) || (this.state.sortType != 3) ? "marginLeft10 fa-solid fa-circle-down" : "hidden"} title="Sort Ascending" onClick={(e) => this.handleSort(e, 3, 1)}></i>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "" : "hidden"}></th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Size</th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Qty Total
                                            <span className="tooltipitemtext tooltipitemtext__right">Total Quantity For Job</span>
                                        </div>
                                    </th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Qty
                                            <span className="tooltipitemtext tooltipitemtext__right">Quantity allocated to truck</span>
                                        </div>
                                    </th>
                                    <th></th>
                                    <th><i className={this.state.canEdit ? "fa-solid fa-share" : "hidden"} onClick={this.moveBranchSelected}></i></th>
                                    <th>Truck</th>
                                    <th className={this.state.systemCompanyId === 3 ? "" : "hidden"}></th>
                                    <th></th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}></th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Team</th>
                                    <th>
                                        <div className="tooltipitem tooltipitem__right">Order
                                            <span className="tooltipitemtext tooltipitemtext__right">Truck Allocation Order</span>
                                        </div>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "textaligncenter" : "hidden"}>
                                        <input className={this.state.canEdit ? "marginRight10" : "hidden"} type='checkbox' id="selectAllPrinted" name="selectAllPrinted" checked={this.state.selectAllPrinted} onChange={(e) => this.selectAllPrintedJobs(e)} />
                                        <div className="tooltipitem tooltipitem__right">Printed
                                            <span className="tooltipitemtext tooltipitemtext__right">Runsheet has been printed</span>
                                        </div>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : ""}>Est. Time</th>
                                    <th className="textaligncenter">Notes</th>
                                    <th>R/O</th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : "textaligncenter"}>
                                        <div className="tooltipitem tooltipitem__left">P/H
                                            <span className="tooltipitemtext tooltipitemtext__left">Create a placeholder job for when this job rolls over</span>
                                        </div>
                                    </th>
                                    <th className={this.state.systemCompanyId === 3 ? "hidden" : "textaligncenter"}>Extend</th>
                                    <th className={this.state.systemCompanyId === 3 ? "textaligncenter" : "hidden"}>Hold</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {allocateItems.map((item, index) =>
                                    <tr className={(item.itemTypeId === 26 || item.itemTypeId === 36) && item.frequencyId === 1 ? "tableColours--pc--monthly" : (item.itemTypeId === 26 || item.itemTypeId === 36) && item.frequencyId === 2 ? "tableColours--pc--fortnightly" : (item.itemTypeId === 26 || item.itemTypeId === 36) && item.frequencyId === 3 ? "tableColours--pc--weekly" : (item.itemTypeId === 26 || item.itemTypeId === 36) ? "tableColours--pc--oneoff" : item.typeId === 1 && item.isPlaceholder ? "tableColours--install-ph" : item.manualItem && this.state.systemCompanyId === 3 ? "tableColours--manual" : item.typeId === 3 ? "tableColours--assist" : item.typeId === 4 ? "tableColours--cleaning" : item.typeId === 5 ? "tableColours--manual" : item.typeId === 1 && item.isCustomerPickup ? "tableColours--cust--install" : item.typeId === 1 ? "tableColours--install" : item.typeId === 2 && item.isPlaceholder ? "tableColours--pickup-ph" : item.typeId === 2 && item.isCustomerPickup ? "tableColours--cust--pickup" : item.typeId === 2 ? "tableColours--pickup" : ""} key={index}>
                                        <td className="hidden">{item.id}</td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>{index + 1}</td>
                                        <td className="table__text--align">
                                            <div>
                                                <a className={item.needsPo && !item.manualItem ? "makeitred tooltipitem tooltipitem__right" : !item.manualItem ? "tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => this.showInfo(e, index)}>
                                                    <span className="fas fa-info-circle copy--icon alignIconCenter"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__right">Booking Information</span>
                                                </a>
                                            </div>
                                        </td>
                                        <td className={this.state.canEdit ? "table__text--align" : "hidden"}>
                                            <input type='checkbox' id="selected" name="selected" className={(item.itemStatusId === 3 || item.itemStatusId === 2) && !item.jobStarted ? '' : "hidden"} checked={item.selected} onChange={(e) => this.onCellCheckboxChange(item.itemIndex, e)} />
                                        </td>
                                        <td className={(item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align") + (item.scheduledDateOriginalRed ? " allocate-pcred" : item.scheduledDateOriginalOrange ? " allocate-pcorange" : "")}>
                                            <input type={this.state.systemCompanyId === 2 && (item.itemStatusId === 3 || item.itemStatusId === 2) && this.state.canEdit && !item.jobStarted ? 'date' : "hidden"} min={minDate} className={"form-control allocate__dateSize" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : "") + (item.customerUpdatesRequired ? " allocate__customerText" : "") + (item.itemStatusId == 2 || item.goaheadRequired ? " allocate__redText" : "")} value={item.scheduleDate} onChange={(e) => this.onScheduleDateCellChange(item.itemIndex, "scheduleDate", e.target.value)} />
                                            {this.state.systemCompanyId === 3 ? item.scheduledDateOriginalFormatted : (item.itemStatusId === 3 || item.itemStatusId === 2) && this.state.canEdit && !item.jobStarted ? "" : item.scheduleDateFormatted}
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <input type={(item.itemStatusId === 3 || item.itemStatusId === 2) && this.state.canEdit && !item.jobStarted && !item.manualItem && !item.allowExtend ? 'date' : "hidden"} min={minDate} className={"form-control allocate__dateSize" + (item.reckonUpdatesRequired ? " allocate__customerReckonText" : "") + (item.customerUpdatesRequired ? " allocate__customerText" : "") + (item.itemStatusId == 2 || item.goaheadRequired ? " allocate__redText" : "")} value={item.typeId === 1 ? item.onhireDate : item.offhireDate} onChange={(e) => this.onCellChange(item.itemIndex, item.typeId === 1 ? "onhireDate" : "offhireDate", e.target.value)} />
                                            {(item.itemStatusId === 3 || item.itemStatusId === 2) && this.state.canEdit && !item.jobStarted && !item.allowExtend ? "" : item.typeId === 1 ? item.onhireDateFormatted : item.offhireDateFormatted}
                                        </td>
                                        <td className="table__text--align">
                                            <Link className={item.jobId == "0" ? "hidden" : ""} to={'/job/' + item.jobId + '/files'} target="_blank">
                                                <i className="fa-solid fa-folder-open alignIconCenter" title="View Job Files"></i>
                                            </Link>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align" : "hidden"}>
                                            {item.customer}
                                        </td>
                                        <td className="table__text--align">
                                            <Link className={item.jobId == "0" ? "hidden" : ""} to={'/job/' + item.jobId} target="_blank">
                                                {item.jobId}
                                            </Link>
                                        </td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <div className={item.jobId == "0" ? "hidden" : item.reckonUpdatesRequired ? "tooltipitem tooltipitem__right allocate__customerReckonText" : item.customerUpdatesRequired ? "tooltipitem tooltipitem__right allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "tooltipitem tooltipitem__right allocate__redText" : "tooltipitem tooltipitem__right"}>{item.siteAddress ? item.siteAddress.substring(0, 60) + "" : ""}
                                                <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                                            </div>
                                            <div className={item.jobId != "0" ? "hidden" : item.itemStatusId == 2 || item.goaheadRequired ? "tooltipitem tooltipitem__right allocate__redText" : "tooltipitem tooltipitem__right"}>{item.siteAddress ? item.siteAddress.substring(0, 60) + "" : ""}
                                                <span className="tooltipitemtext tooltipitemtext__right">{item.siteAddress}</span>
                                            </div>
                                            <a className={item.itemStatusId === 3 && this.state.systemCompanyId === 3 ? "tooltipitem tooltipitem__right marginLeft10" : "hidden"} href="#" onClick={(e) => this.editDelAddress(e, item.itemIndex)}>
                                                <span className="fas fa-edit copy--icon"></span>
                                                <span className="tooltipitemtext tooltipitemtext__right">Edit Delivery Address</span>
                                            </a>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align" : "hidden"}>
                                            {item.suburb}
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            {item.linkedJobId === 0 ? item.truckAssistScheduleId === 0 ? "" : "Assist" : item.linkedJobId}
                                        </td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <div className="tooltipitem tooltipitem__right">{item.manualItem || (item.callout && item.description) ? item.description.substring(0, 10) + "... " : item.itemShortcut}
                                                <span className="tooltipitemtext tooltipitemtext__right">{item.manualItem || (item.callout && item.description) ? item.description : item.invoiceDescription != "" ? item.invoiceDescription : item.item}</span>
                                            </div>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                            <div className={item.hasAssociatedItems ? "" : "hidden" }>
                                                <a className={item.hasAssociatedItemsToComplete ? "makeitlightorange tooltipitem tooltipitem__right" : "tooltipitem tooltipitem__right"} href="#" onClick={(e) => this.showAssociatedJobs(e, index)}>
                                                    <span className="fa-solid fa-circle-exclamation copy--icon alignIconCenter"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__right">Icon will be orange if there are associated item that need to be completed or blue if they have been completed (Click to View Items).</span>
                                                </a>
                                            </div>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align table--cell--nowrap"}>
                                            {item.bays === 0 && item.size === 0 ? "" : item.sizeBays}
                                        </td>
                                        <td className={"table__text--align textaligncenter " + (item.reckonUpdatesRequired ? "allocate__customerReckonText" : item.customerUpdatesRequired ? "allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "allocate__redText" : "")} >
                                            {item.itemTypeId === 53 && item.typeId === 1 ? "" : item.quantityTotal}
                                            <input type='number' min={0} step={1} className={item.itemTypeId === 53 && item.typeId === 1 ? "form-control allocate__qtySize" : "hidden"} value={item.quantityTotal} onChange={(e) => this.onCellChange(item.itemIndex, "quantityTotal", e.target.value)} disabled={!item.allowEdit || !this.state.canEdit || item.runsheetChecked || (item.typeId === 1 && item.goaheadRequired)} />
                                        </td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <input type={(!item.itemIsKwikstage && !item.isPlaceholder && this.state.canEdit) ? 'number' : "hidden"} min={0} step={1} className={item.reckonUpdatesRequired ? "form-control allocate__qtySize allocate__customerReckonText" : item.customerUpdatesRequired ? "form-control allocate__qtySize allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "form-control allocate__qtySize allocate__redText" : "form-control allocate__qtySize"} value={item.quantityScheduled} onChange={(e) => this.onCellChange(item.itemIndex, "quantityScheduled", e.target.value)} disabled={!item.allowEdit || !this.state.canEdit || item.runsheetChecked || item.itemTypeId === 23 || item.itemTypeId === 26 || (item.typeId === 1 && item.goaheadRequired) || (this.state.systemCompanyId === 3 && item.quantityTotal === 1 && (item.itemTypeId != 53 || item.typeId === 2))} />
                                            {this.state.canEdit && !item.itemIsKwikstage && !item.isPlaceholder ? "" : item.quantityScheduled}
                                            <a className={this.state.canEdit && !item.customerUpdatesRequired && !item.reckonUpdatesRequired && !item.runsheetChecked && item.itemIsKwikstage && (item.typeId === 2 || (item.typeId === 1 && !item.goaheadRequired)) ? "makeitgreen" : "hidden"} href="#" onClick={(e) => this.calculateKwikstage(e, item.itemIndex)}>
                                                <span className="fas fa-calculator edit--icon marginLeft10"></span>
                                            </a>
                                        </td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>{item.unitOfMeasure}</td>
                                        <td className="table__text--align">
                                            <div className={this.state.canEdit && item.itemStatusId == 3 && !item.jobStarted ? "" : "hidden"}>
                                                <a className="tooltipitem tooltipitem__right alignIconCenter" href="#" onClick={(e) => this.changeBranch(e, item.itemIndex)}>
                                                    <span className="fas fa-share copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__right">Move job to another branch</span>
                                                </a>
                                            </div>
                                        </td>
                                        <td className={item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <select className={this.state.canEdit && item.allowEdit && !item.jobStarted ? item.reckonUpdatesRequired ? "select allocate__customerReckonText" : item.customerUpdatesRequired ? "select allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "select allocate__redText" : "select" : "hidden"} id="truckId" name="truckId" value={item.truckId} onChange={(e) => this.onTruckCellChange(item.itemIndex, e)} disabled={item.allowEdit && this.state.canEdit ? false : true}>
                                                <option defaultValue="-1"></option>
                                                {truckOptions.map(truck =>
                                                    <option key={truck.id} value={truck.id}>{truck.name}-{truck.registrationNumber}</option>
                                                )};
                                            </select>
                                            {this.state.canEdit && item.allowEdit && !item.jobStarted ? "" : item.truck}
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                            <span className={item.craneTruckRequired ? "alignIconCenter tooltipitem tooltipitem__right" : "hidden"}>
                                                <span className="fa-solid fa-circle-exclamation copy--icon makeitred"></span>
                                                <span className="tooltipitemtext tooltipitemtext__right">Crane Truck Required</span>
                                            </span>
                                        </td>
                                        <td className="table__text--align">
                                            <div className={this.state.canEdit && item.allowEdit && !item.reckonUpdatesRequired && !item.goaheadRequired && item.itemStatusId == 3 && item.truckId > 0 && item.quantityTotal > 1 ? "" : "hidden"}>
                                                <a className={this.state.systemCompanyId === 2 ? "tooltipitem tooltipitem__right alignIconCenter" : "hidden"} href="#" onClick={(e) => this.allocateMultipleTrucks(e, item.itemIndex)}>
                                                    <span className="fas fa-link copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__right">Allocate multiple trucks to job</span>
                                                </a>
                                                <a className={this.state.systemCompanyId === 3 ? "tooltipitem tooltipitem__right alignIconCenter" : "hidden"} href="#" onClick={(e) => this.allocateMultiTrucksSimple(e, item.itemIndex)}>
                                                    <span className="fas fa-link copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__right">Allocate multiple trucks to job</span>
                                                </a>
                                            </div>
                                            
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : "table__text--align"}>
                                            <div className={this.state.canEdit && !item.customerUpdatesRequired && !item.reckonUpdatesRequired && !item.goaheadRequired && item.itemStatusId == 3 && item.truckId > 0 ? "" : "hidden"}>
                                                <a className="tooltipitem tooltipitem__right alignIconCenter" href="#" onClick={(e) => this.sendTrucksToAssist(e, item.itemIndex)}>
                                                    <span className="fas fa-truck-moving copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__right">Send Truck/s to assist on job</span>
                                                </a>
                                            </div>

                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <div className="tooltipitem tooltipitem__left">{item.teamDisplay ? item.teamDisplay.substring(0, 12) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__left">{item.teamDisplay}</span>
                                            </div>
                                        </td>
                                        <td className="table__text--align">
                                            <input type={this.state.canEdit && !item.jobStarted ? 'number' : "hidden"} className={item.reckonUpdatesRequired ? "form-control allocate__orderSize allocate__customerReckonText" : item.customerUpdatesRequired ? "form-control allocate__orderSize allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? 'form-control allocate__orderSize allocate__redText' : "form-control allocate__orderSize"} min={1} step={1} value={item.vehicleItemOrder} onChange={(e) => this.onCellChange(item.itemIndex, "vehicleItemOrder", e.target.value)} disabled={item.truckId <= 0 || !item.allowEdit ? true : false} />
                                            {this.state.canEdit && !item.jobStarted ? "" : item.vehicleItemOrder}
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "table__text--align textaligncenter" : "hidden"}>
                                            <input type='checkbox' id="printed" name="printed" checked={item.printed} onChange={(e) => this.onCellCheckboxChange(item.itemIndex, e)} disabled={(item.truckId < 0 && !item.printed) || !this.state.canEdit} />
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : item.reckonUpdatesRequired ? "table__text--align allocate__customerReckonText" : item.customerUpdatesRequired ? "table__text--align allocate__customerText" : item.itemStatusId == 2 || item.goaheadRequired ? "table__text--align allocate__redText" : "table__text--align"}>
                                            <input type={this.state.canEdit ? 'text' : "hidden"} pattern="(0?[0-9]|1[0-2]):[0-5][0-9]$" className={item.itemStatusId == 2 || item.goaheadRequired ? "form-control allocate__estTimeSize allocate__redText" : "form-control allocate__estTimeSize"} value={item.estimatedTime} onChange={(e) => this.onCellChange(item.itemIndex, "estimatedTime", e.target.value)} disabled={item.allowEdit ? false : true} />
                                            {this.state.canEdit ? "" : item.estimatedTime}
                                        </td>
                                        <td className="table__text--align allocate-notes--width">
                                            <a className={item.notes == "" ? "hidden" : item.showRedNotes && !item.manualItem ? " makeitred" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, item.itemIndex)}>
                                                <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.length < 60 ? item.notes : item.notes.substring(0, 60) + "... " : ""}
                                                    <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{item.notes}</span>
                                                </div>
                                            </a>
                                            <a className={item.notes != "" ? "hidden" : item.showRedNotes ? " makeitred" : item.showGreenNotes ? " makeitgreen" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => this.editNotes(e, item.itemIndex)}>
                                                <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            </a>
                                        </td>
                                        <td className="table__text--align textaligncenter">
                                            <div className={this.state.canEdit && item.allowEdit && !item.reckonUpdatesRequired && !item.goaheadRequired && item.itemStatusId == 3 && !item.jobStarted && item.quantityScheduled > 1 ? "copy--tablecell" : "hidden"}>
                                                <a className="tooltipitem tooltipitem__left alignIconCenter" href="#" onClick={(e) => this.copyItem(e, item.itemIndex)}>
                                                    <span className="fas fa-copy copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__left">Rollover job to another day</span>
                                                </a>
                                            </div>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" : "table__text--align"}>
                                            <div className={this.state.canEdit && !item.reckonUpdatesRequired && !item.goaheadRequired && item.itemStatusId == 3 && item.quantityScheduled > 1 ? "copy--tablecell" : "hidden"}>
                                                <a className={item.isPlaceholder ? "hidden" : "tooltipitem tooltipitem__left alignIconCenter"} href="#" onClick={(e) => this.createPlaceholder(e, item.itemIndex)}>
                                                    <span className="fa-solid fa-fire-flame-simple fa-rotate-180 copy--icon"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__left">Create a placeholder job for when job rolls over</span>
                                                </a>
                                            </div>
                                            <input type='checkbox' className={item.isPlaceholder ? "input alignIconCenter" : "hidden"} checked={true} disabled />
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "hidden" :"table__text--align"}>
                                            <a className={this.state.canEdit && item.allowExtend && !item.isPlaceholder ? "tooltipitem tooltipitem__left alignIconCenter" : "hidden"} data-toggle="modal" data-target="#extendModal" href="#" onClick={(e) => this.extendModal(e, item.itemIndex)}>
                                                <span className="fas fa-arrow-right copy--icon"></span>
                                                <span className="tooltipitemtext tooltipitemtext__left">Extend Hire Period (USE CAUTION)</span>
                                            </a>
                                        </td>
                                        <td className={this.state.systemCompanyId === 3 ? "table__text--align textaligncenter" : "hidden"}>
                                            <a className={item.allowOnHold && !item.isPlaceholder && !item.manualItem && item.truckId < 1 ? "tooltipitem tooltipitem__left alignIconCenter" : "hidden"} href="#" onClick={(e) => this.placeJobOnHold(e, item.itemIndex)}>
                                                <i className="fa-solid fa-circle-pause edit--icon--large"></i>
                                                <span className="tooltipitemtext tooltipitemtext__left">Put job on hold</span>
                                            </a>
                                        </td>
                                        <td className="table__text--align textaligncenter">  
                                            {((item.manualItem && item.allowDelete) || (this.state.canEdit && item.allowDelete)) ?
                                                <div className="delete--tablecell">
                                                    <a className="makeitred" href="#" onClick={(e) => this.cancelItem(e, item.itemIndex)}>
                                                        <span className="fas fa-times-circle delete--icon alignIconCenter"></span>
                                                    </a>
                                                </div> : ""
                                            }
                                        </td>
                                    </tr>

                                )}
                            </tbody>
                        </table>
                        {this.state.errors["allocatedItems"] ?
                            (this.state.errors["allocatedItems"]).split("\n").map((item: any, key: any) => {
                                return <span className="label errors" key={key}>{item}<br /></span>
                            })
                            : ""}
                        {warnings.length > 0 ?
                                warnings.map((item, index) =>
                                    <span className="label errors" key={index}>{'Warning: ' + DateHelpers.formatDate(item.scheduleDate) + ' is ' + (item.locked ? ' locked. Please unlock day or selected another date' : ' a public holiday or within a shutdown period')}<br /></span>
                                )
                             : ""}
                    </div>
                    
                </div>
                <div className="makeitflexspacebetween">
                    <div className={this.state.canEdit ? "hidden" : "" }></div>
                    <NavLink className={this.state.systemCompanyId === 2 && this.state.canEdit ? "" : "hidden"} to={'/allocate/map/' + this.props.scheduleDate} activeClassName='active'>
                        <span className='fas fa-align-justify edit--icon'></span> View on Map
                    </NavLink>
                    <NavLink target="_blank" className={this.state.systemCompanyId === 3 && this.state.canEdit ? "" : "hidden"} to={'/allocate/googlemap/' + this.props.scheduleDate + '/' + this.props.itemTypeGroupFilterId} activeClassName='active'>
                        <span className='fas fa-align-justify edit--icon'></span> View on Map
                    </NavLink>
                    <div>Total Jobs: {this.state.totalJobs}</div>
                </div>
                <div className="input-group-parent">
                    <div className="marginBottom10">
                        <button className="defaultbutton defaultbutton__large" type="button" onClick={this.printRunSheets} disabled={!this.state.canEdit}>Print Run Sheets</button>
                        <button className={this.state.systemCompanyId === 3 ? "defaultbutton defaultbutton__large marginLeft10" : "hidden"} type="button" onClick={this.printRunSheetsPrinted} disabled={!this.state.canEdit}>Print Run Sheets inc Printed</button>
                        <button className="defaultbutton defaultbutton__large marginLeft15" type="button" onClick={this.printRunSheetSummary}>Run Sheet Summary</button>
                    </div>
                </div>
                <div className="input-group-parent">
                    <div className={this.state.canEdit ? "colAlignRight marginBottom10" : "hidden"}>
                        <button className="defaultbutton defaultbutton__large" type="button" onClick={this.submit}>Save</button>
                    </div>
                </div>
                <div>
                    <a className={this.state.showColourKey ? "hidden" : ""} href="#" onClick={(e) => this.showJobColourKey(e, true)}>Show Colour Key</a>
                    <a className={this.state.showColourKey ? "" : "hidden"} href="#" onClick={(e) => this.showJobColourKey(e, false)}>Hide Colour Key</a>
                </div>
                <div className={this.state.showColourKey ? "marginBottom10" : "hidden"}>
                    <div className="allocate-key-item tableColours--install">Install Job</div>
                    <div className="allocate-key-item tableColours--pickup">Pickup Job</div>
                    <div className="allocate-key-item tableColours--assist">Assist Job</div>
                    <div className="allocate-key-item tableColours--install-ph">Install Job Placeholder</div>
                    <div className="allocate-key-item tableColours--pickup-ph">Pickup Job Placeholder</div>
                    <div className="allocate-key-item tableColours--manual">Manual Job</div>
                    <div className={this.state.systemCompanyId === 3 ? "" : "hidden" }>
                        <div className="allocate-key-item tableColours--cleaning">Cleaning Job</div>
                        <div className="allocate-key-item tableColours--cust--install">Customer Picking Up Item</div>
                        <div className="allocate-key-item tableColours--cust--pickup">Customer Delivering Item Back to SS</div>
                        <div className="allocate-key-item tableColours--pc--oneoff">Pump & Clean - One Off Service</div>
                        <div className="allocate-key-item tableColours--pc--monthly">Pump & Clean - Monthly Service</div>
                        <div className="allocate-key-item tableColours--pc--fortnightly">Pump & Clean - Fortnightly Service</div>
                        <div className="allocate-key-item tableColours--pc--weekly">Pump & Clean - Weekly Service</div>
                    </div>
                </div>
            </div>
        );

    }

};